/* global globalThis */
/**
 * MemberDetailsPage component displays detailed information about a specific member
 * including their personal information, contact details, notes, opportunities, and program status.
 *
 * Features:
 * - Displays member overview with personal and contact information
 * - Shows program opportunities and monitoring status
 * - Manages member notes with CRUD operations
 * - Tracks member interactions and dispositions
 * - Handles member data fetching and updates
 *
 * @returns {JSX.Element} The rendered member details page
 */
/* eslint-disable no-console, no-unused-vars */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import EditContactInfoModal from "../components/modals/EditContactInfoModal";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { addNoteApi, editNoteApi, fetchNotes } from "../api/notes";
import {
  fetchEscalations,
  fetchEscalationVersions,
  createEscalation,
  updateEscalation,
  createContactAttempt,
} from "../api/escalation";
import {
  Box,
  Typography,
  Chip,
  Container,
  styled,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Lottie from "lottie-react";
import loaderAnimation from "../assets/loader/loader.json";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { formatPhoneNumber } from "../utils/formatUtils";
import { formatDecimalNumber } from "../utils/currency";
import {
  getNameVariants,
  maskAllButLast,
  maskEmail,
  maskAll,
  maskShowInitialOnly,
  maskCity,
  maskAddressLine,
  maskState,
  maskPostalCode,
  maskDOB,
} from "../utils/maskedData";

import Breadcrumb from "../components/common/Breadcrumb";
import AddNoteModal from "../components/modals/AddNoteModal";
import { getRelCodeLabel } from "../constants/relCode";
import CreateEscalationModal from "../components/modals/CreateEscalationModal";
import { TERMINAL_ESCALATION_STATUSES } from "../constants/escalation";
import EscalationVersionHistoryModal from "../components/modals/EscalationVersionHistoryModal";
import AddEditOutcomeModal from "../components/modals/AddEditOutcomeModal";
import OpportunitiesTable from "../components/opportunities/OpportunitiesTable";
import CopayCardModal from "../components/modals/CopayCardModal";
import ClinicalNotePage from "./clinicalNote/ClinicalNotePage";
import ClinicalHistoryModal from "../components/modals/ClinicalHistoryModal";
import ClinicalNoteStartModal from "../components/modals/ClinicalNoteStartModal";
import BiosimilarsOutcomeModal from "../components/modals/BiosimilarsOutcomeModal";
import {
  fetchAssessmentGroups,
  fetchAssessmentReferenceData,
  getCachedAssessmentReferenceData,
  initializeAssessmentApi,
  cleanProgramId,
  checkIsFormless,
  hasTerminalOutcome,
  isNonClinicalProgram,
} from "../api/assessment";
import { fetchMemberClinicalPrograms } from "../api/oppurtunity";

import {
  fetchMemberOverview,
  fetchFamilyBySubscriber,
  updateMemberPersonalInfo,
  updateMemberContactInfo,
  resyncOutreachCache,
} from "../api/priority";
import { fetchPriorAuths } from "../api/priorAuth";
import { searchSubscribers } from "../api/subscriber";
import {
  linkColor,
  linkHoverColor,
  placeholderText,
} from "../constants/colors";
import { getOutcomesOutreach } from "../api/outcomesOutreach";
import { fetchOopDetails } from "../api/oop";
import { LoadingWaveDots } from "../components/common/Loader/LoadingWave";

import { useLoading } from "../context/LoadingContext";
import { capitalizeFirstLetter } from "../utils/capitalize";
import { usePermissions } from "../redux/permissions";
import { useSelector } from "react-redux";
import { requestCopayCard } from "../api/aedificare";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

/**
 * Estimates the diagonal screen size in inches based o screen resolution and DPI
 * @returns {number} Diagonal screen size in inches
 */
function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

function formatDate(dateValue) {
  if (dateValue == null || dateValue === "") return "N/A";
  try {
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return dateValue;
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch (e) {
    console.error("Error formatting date:", e);
    return dateValue;
  }
}

const checkBiosimilar = (val) => {
  if (!val) return false;
  if (typeof val === "string") return val.toUpperCase().includes("BIOSIMILAR");
  if (typeof val === "object") {
    return (
      checkBiosimilar(val.programId) ||
      checkBiosimilar(val.programName) ||
      checkBiosimilar(val.programDisplayName) ||
      checkBiosimilar(val.displayName) ||
      checkBiosimilar(val.name) ||
      checkBiosimilar(val.groupName) ||
      checkBiosimilar(val.program) ||
      checkBiosimilar(val.id) ||
      checkBiosimilar(val.canonicalId)
    );
  }
  return false;
};

const ContentContainer = styled("div")(() => ({
  flexGrow: 1,
  width: "100%",
  backgroundColor: "#F7F8FB",
  display: "flex",
  flexDirection: "column",
  paddingTop: "10px",
}));

const determineContactDisplay = (memberData, isShowPhiEnabled) => {
  const hasPrimaryPhone = !!(
    memberData.phone || memberData.preferredPhoneNumber
  );
  const hasPrimaryEmail = !!memberData.email;
  const hasAlternatePhone = !!(
    memberData.alternatePhoneNum || memberData.alternatePhoneNumber
  );
  const hasAlternateEmail = !!(
    memberData.alternateEmailAddress || memberData.alternateEmail
  );

  const preferredMethod = String(memberData.preferredContactMethod || "")
    .toLowerCase()
    .trim();

  let contactValue = "N/A";
  let showLanguage = false;

  const isAlternateEmail =
    preferredMethod === "alternate email" ||
    preferredMethod === "alternate email address" ||
    preferredMethod === "alternate_email" ||
    preferredMethod === "alternateemail" ||
    (preferredMethod.includes("alternate") &&
      preferredMethod.includes("email"));

  const isAlternatePhone =
    preferredMethod === "alternate phone" ||
    preferredMethod === "alternate phone number" ||
    preferredMethod === "alternate_phone" ||
    preferredMethod === "alternatephone" ||
    (preferredMethod.includes("alternate") &&
      preferredMethod.includes("phone"));

  const isPrimaryEmail =
    !isAlternateEmail &&
    (preferredMethod === "email" ||
      preferredMethod === "email address" ||
      preferredMethod === "emailaddress" ||
      (preferredMethod.includes("email") &&
        !preferredMethod.includes("alternate")));

  if (
    (isAlternateEmail && hasAlternateEmail) ||
    (!isAlternatePhone &&
      !isPrimaryEmail &&
      !hasPrimaryPhone &&
      hasAlternateEmail)
  ) {
    contactValue = maskEmail(
      memberData.alternateEmailAddress || memberData.alternateEmail || "N/A",
      isShowPhiEnabled,
    );
  } else if (
    (isAlternatePhone && hasAlternatePhone) ||
    (!isPrimaryEmail && !hasPrimaryPhone && hasAlternatePhone)
  ) {
    contactValue = formatPhoneNumber(
      memberData.alternatePhoneNum || memberData.alternatePhoneNumber || "N/A",
      isShowPhiEnabled,
    );
  } else if (
    (isPrimaryEmail && hasPrimaryEmail) ||
    (!hasPrimaryPhone && hasPrimaryEmail)
  ) {
    contactValue = maskEmail(memberData.email || "N/A", isShowPhiEnabled);
  } else if (hasPrimaryPhone) {
    contactValue = formatPhoneNumber(
      memberData.preferredPhoneNumber || memberData.phone || "N/A",
      isShowPhiEnabled,
    );
  }

  showLanguage = !!memberData.language;

  return { contactValue, showLanguage };
};

const ContactInfo = ({ memberData, isShowPhiEnabled }) => {
  const { contactValue } = determineContactDisplay(
    memberData,
    isShowPhiEnabled,
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        maxWidth: "200px",
        minWidth: 0, // Allows the flex item to shrink below its minimum content size
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          flex: 1,
        }}
      >
        <Tooltip
          title={contactValue === "N/A" ? "" : contactValue}
          arrow
          placement="top"
          disableHoverListener={contactValue === "N/A"}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#403C6F",
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "20px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              display: "inline-block",
              verticalAlign: "bottom",
              flexShrink: 1,
              minWidth: 0, // Allows the text to shrink and ellipsis to work
            }}
          >
            {contactValue}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

ContactInfo.propTypes = {
  memberData: PropTypes.shape({
    phone: PropTypes.string,
    email: PropTypes.string,
    preferredPhoneNumber: PropTypes.string,
    preferredContactMethod: PropTypes.string,
    alternatePhoneNum: PropTypes.string,
    alternatePhoneNumber: PropTypes.string,
    alternateEmailAddress: PropTypes.string,
    alternateEmail: PropTypes.string,
    language: PropTypes.string,
  }).isRequired,
  isShowPhiEnabled: PropTypes.bool.isRequired,
};

const matchesNavState = (state, flag, source) =>
  Boolean(state?.[flag]) || state?.source === source;

const pathMatches = (pathname, lastVisitedPage, segment) =>
  pathname.includes(segment) ||
  lastVisitedPage?.includes(segment) ||
  document.referrer.includes(segment);

const resolveFromMemberMonitoring = (state, pathname, lastVisitedPage) => {
  if (matchesNavState(state, "fromMemberMonitoring", "monitoring")) {
    return true;
  }
  if (
    matchesNavState(state, "fromPriorityOutreach", "member-outreach") ||
    matchesNavState(state, "fromAllMembers", "all-members")
  ) {
    return false;
  }
  return pathMatches(pathname, lastVisitedPage, "/monitoring");
};

const resolveFromAllMembers = (state, pathname, lastVisitedPage) => {
  if (matchesNavState(state, "fromAllMembers", "all-members")) {
    return true;
  }
  if (
    matchesNavState(state, "fromMemberMonitoring", "monitoring") ||
    matchesNavState(state, "fromPriorityOutreach", "priority-outreach")
  ) {
    return false;
  }
  return pathMatches(pathname, lastVisitedPage, "/all-members");
};

const resolveFromPriorityOutreach = (state, pathname, lastVisitedPage) => {
  if (matchesNavState(state, "fromPriorityOutreach", "member-outreach")) {
    return true;
  }
  if (
    matchesNavState(state, "fromMemberMonitoring", "monitoring") ||
    matchesNavState(state, "fromAllMembers", "all-members")
  ) {
    return false;
  }
  return pathMatches(pathname, lastVisitedPage, "/member-outreach");
};

const resolveFromManualTasks = (state, pathname, lastVisitedPage) =>
  matchesNavState(state, "fromManualTasks", "manual-tasks") ||
  pathMatches(pathname, lastVisitedPage, "/manual-tasks");

const resolveLastVisitedPage = ({
  fromMemberMonitoring,
  fromPriorityOutreach,
  fromManualTasks,
}) => {
  if (fromMemberMonitoring) return "/members/monitoring";
  if (fromPriorityOutreach) return "/members/member-outreach";
  if (fromManualTasks) return "/manual-tasks";
  return "/members/member-outreach";
};

const MODULE_NAME_BY_SOURCE = {
  "member-outreach": "Member Outreach",
  monitoring: "Member Monitoring",
  "all-members": "Member Outreach",
  "manual-tasks": "Member Outreach",
};

const resolveModuleName = (
  source,
  {
    fromPriorityOutreach,
    fromMemberMonitoring,
    fromAllMembers,
    fromManualTasks,
  },
) => {
  if (source && MODULE_NAME_BY_SOURCE[source]) {
    return MODULE_NAME_BY_SOURCE[source];
  }
  if (fromPriorityOutreach) return "Member Outreach";
  if (fromMemberMonitoring) return "Member Monitoring";
  if (fromAllMembers || fromManualTasks) return "Member Outreach";
  return "Members";
};

const getCopayOppId = (opp) =>
  opp.programId || opp.memberProgramId || opp.opportunityId || opp.id;

const mapCopayData = (copayData) => ({
  ...copayData,
  bin: copayData.bin || "",
  pcn: copayData.pcn || "",
  grpId: copayData.grpId || "",
  memberId: copayData.memberId || "",
  expirationDate: copayData.expirationDate || "",
  executionStatus: copayData.executionStatus || "",
  registrationRequestStatus: copayData.registrationRequestStatus || "",
  copayBin: copayData.bin || "",
  copayPcn: copayData.pcn || "",
  copayGrpId: copayData.grpId || "",
  copayMemberId: copayData.memberId || "",
  copayExpirationDate: copayData.expirationDate || "",
  copayExecutionStatus: copayData.executionStatus || "",
  copayRegistrationRequestStatus: copayData.registrationRequestStatus || "",
  annualBenefitAmount:
    copayData.annualBenefitAmount === undefined
      ? null
      : copayData.annualBenefitAmount,
  annualBenefitExpirationDate: copayData.annualBenefitExpirationDate || "",
  annualBenefitNotes:
    copayData.annualBenefitNotes || copayData.annualBenefitNote || "",
});

const extractProgramList = (fetched) => {
  if (Array.isArray(fetched)) return fetched;
  if (Array.isArray(fetched?.content)) return fetched.content;
  if (Array.isArray(fetched?.programs)) return fetched.programs;
  if (Array.isArray(fetched?.data)) return fetched.data;
  return [];
};

const getDraftAssessmentId = (draftInfo) =>
  draftInfo?.clinicalAssessmentId ||
  draftInfo?.assessmentId ||
  draftInfo?.batchId;

const getSectionArrowTransform = (isOpen) =>
  isOpen ? "rotate(0deg)" : "rotate(-90deg)";

const getMemberIdDisplay = (memberData, isShowPhiEnabled) =>
  memberData.subscriberNum
    ? maskAllButLast(memberData.subscriberNum, 2, isShowPhiEnabled)
    : "N/A";

const getAgeGenderDisplay = (memberData, isShowPhiEnabled) =>
  `${maskAll(memberData.age, isShowPhiEnabled) || "N/A"} / ${
    memberData.sex
      ? capitalizeFirstLetter(maskAll(memberData.sex, isShowPhiEnabled))
      : "N/A"
  }`;

const getNameWithCodeDisplay = (name, code) =>
  name ? capitalizeFirstLetter(name) + ` (${code})` : "N/A";

const resolvePerFillAmount = (details, opp) => {
  if (details.perFillAmount !== undefined) return details.perFillAmount;
  if (details.copayPerFillAmount !== undefined)
    return details.copayPerFillAmount;
  if (opp?.perFillAmount !== undefined) return opp?.perFillAmount;
  return opp?.program?.perFillAmount;
};

const getCapitalizedOrNA = (value) =>
  value ? capitalizeFirstLetter(value) : "N/A";

const getTextConsentDisplay = (textingConsentObtained) =>
  textingConsentObtained ? "Text consent obtained" : "Text consent missing";

const getBreadcrumbBoxSx = (showPcaDirective) => ({
  flex: showPcaDirective ? { xs: "1 1 auto", md: "0 0 32%" } : "0 0 auto",
  maxWidth: showPcaDirective ? { md: "400px" } : "none",
  minWidth: showPcaDirective ? { xs: 0, md: "280px" } : 0,
  flexShrink: 0,
  overflow: "hidden",
});

const getChipRowSx = (wrapChipsBelow) => ({
  display: "flex",
  flexDirection: wrapChipsBelow ? "column" : "row",
  alignItems: wrapChipsBelow ? "flex-start" : "center",
  gap: wrapChipsBelow ? 0.8 : 1,
  rowGap: 0.8,
  flexWrap: "wrap",
});

const fetchCopayDetailForOpportunity = async (opp, setCopayDetailsMap) => {
  const oppId = getCopayOppId(opp);
  if (!oppId) return;
  try {
    const copayData = await requestCopayCard(oppId);
    if (copayData) {
      setCopayDetailsMap((prev) => ({
        ...prev,
        [oppId]: mapCopayData(copayData),
      }));
    }
  } catch (err) {
    console.error(`Failed to fetch copay details status for ${oppId}:`, err);
    setCopayDetailsMap((prev) => ({
      ...prev,
      [oppId]: {
        copayExecutionStatus: "FAILED",
      },
    }));
  }
};

const MemberDetails = () => {
  const location = useLocation();

  /**
   * Tracks the last visited page from session storage
   * Used to maintain navigation context when returning from member details
   * @type {string}
   */
  const lastVisitedPage = sessionStorage.getItem("lastVisitedPage");

  // Determine the source of navigation with priority given to location.state
  /**
   * Determines if the navigation originated from the Member Monitoring section
   * @type {boolean}
   */
  const fromMemberMonitoring = useMemo(
    () =>
      resolveFromMemberMonitoring(
        location.state,
        location.pathname,
        lastVisitedPage,
      ),
    [location.state, location.pathname, lastVisitedPage],
  );
  /**
   * Determines if the navigation originated from the All Members section
   * @type {boolean}
   */
  const fromAllMembers = useMemo(
    () =>
      resolveFromAllMembers(location.state, location.pathname, lastVisitedPage),
    [location.state, location.pathname, lastVisitedPage],
  );

  /**
   * Determines if the navigation originated from the Priority Outreach section
   * @type {boolean}
   */
  const fromPriorityOutreach = useMemo(
    () =>
      resolveFromPriorityOutreach(
        location.state,
        location.pathname,
        lastVisitedPage,
      ),
    [location.state, location.pathname, lastVisitedPage],
  );

  /**
   * Determines if the navigation originated from the Manual Tasks section
   * @type {boolean}
   */
  const fromManualTasks = useMemo(
    () =>
      resolveFromManualTasks(
        location.state,
        location.pathname,
        lastVisitedPage,
      ),
    [location.state, location.pathname, lastVisitedPage],
  );

  useEffect(() => {
    sessionStorage.setItem(
      "lastVisitedPage",
      resolveLastVisitedPage({
        fromMemberMonitoring,
        fromPriorityOutreach,
        fromManualTasks,
      }),
    );
  }, [
    fromMemberMonitoring,
    fromPriorityOutreach,
    fromAllMembers,
    fromManualTasks,
  ]);

  const { memberId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (memberId) {
      const decodedMemberId = decodeURIComponent(memberId).trim();

      // Before hyphen characters needs to alphabet or numeric.
      // This regex ensures it starts with alphanumeric and follows with alphanumeric/hyphens.
      const isValid = /^[a-zA-Z0-9][a-zA-Z0-9-]*$/.test(decodedMemberId);

      if (!isValid) {
        void navigate("/not-found", { replace: true });
      }
    }
  }, [memberId, navigate]);

  const { showLoading, hideLoading } = useLoading();

  const moduleNameForPermissions = useMemo(() => {
    return resolveModuleName(location.state?.source, {
      fromPriorityOutreach,
      fromMemberMonitoring,
      fromAllMembers,
      fromManualTasks,
    });
  }, [
    location.state?.source,
    fromPriorityOutreach,
    fromMemberMonitoring,
    fromAllMembers,
    fromManualTasks,
  ]);

  const { canWrite: canWriteModule, canDelete: canDeleteModule } =
    usePermissions(moduleNameForPermissions);

  const [dispositionOpportunity, setDispositionOpportunity] = useState(null);
  const [isCopayCardModalOpen, setIsCopayCardModalOpen] = useState(false);
  const [selectedCopayOpportunity, setSelectedCopayOpportunity] =
    useState(null);
  const [copayOpportunity, setCopayOpportunity] = useState(null);
  const [copayOpportunities, setCopayOpportunities] = useState([]);
  const [copayDetailsMap, setCopayDetailsMap] = useState({});
  const [isCopayCardModalViewOnly, setIsCopayCardModalViewOnly] =
    useState(false);
  const [copayCardModalInitialData, setCopayCardModalInitialData] =
    useState(null);
  const [copayScenario] = useState("scenario1");

  const [outreachData, setOutreachData] = useState({
    outcomes: [],
    outreachMethods: [],
  });
  const [selectedNoteType, setSelectedNoteType] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [containerScale, setContainerScale] = useState(1);
  const [programOptions, setProgramOptions] = useState([]);
  const [monitoringReasons, setMonitoringReasons] = useState([]);
  const [programDrugMap, setProgramDrugMap] = useState({});
  const [programMap, setProgramMap] = useState({});
  const [externalOutcomeTriggerKey, setExternalOutcomeTriggerKey] = useState(0);
  const [opportunitiesReloadKey, setOpportunitiesReloadKey] = useState(0);
  const [isOpportunitiesLoading, setIsOpportunitiesLoading] = useState(true);
  // null until the opportunities fetch resolves at least once, so "No open
  // opportunities." (below, next to the member id) never flashes before we
  // actually know - it should only reflect real data, not the memberId's
  // format (a synthetic "{subscriberNum}-{personCode}" id doesn't mean the
  // member has no opportunities, just that they have no HALO member id yet).
  const [totalOpenOpportunities, setTotalOpenOpportunities] = useState(null);
  const [isMedicationHistoryLoading, setIsMedicationHistoryLoading] =
    useState(false);
  const [isClinicalNoteView, setIsClinicalNoteView] = useState(false);
  const clinicalNoteRef = useRef(null);
  const [showAssessmentDiscardWarning, setShowAssessmentDiscardWarning] =
    useState(false);
  const pendingBreadcrumbActionRef = useRef(null);

  const confirmBreadcrumbNavigation = (action) => {
    if (isClinicalNoteReadOnly) {
      action?.();
      return;
    }
    if (clinicalNoteRef.current?.requestCloseWithAction) {
      clinicalNoteRef.current.requestCloseWithAction(action);
      return;
    }
    pendingBreadcrumbActionRef.current = action;
    setShowAssessmentDiscardWarning(true);
  };
  const [isDraftSession, setIsDraftSession] = useState(false);
  const [isClinicalNoteReadOnly, setIsClinicalNoteReadOnly] = useState(false);
  const [isClinicalHistoryOpen, setIsClinicalHistoryOpen] = useState(false);
  const [isClinicalNotePopupOpen, setIsClinicalNotePopupOpen] = useState(false);
  const [isBiosimilarsOutcomeOpen, setIsBiosimilarsOutcomeOpen] =
    useState(false);
  const [selectedBiosimilarsNote, setSelectedBiosimilarsNote] = useState(null);
  const [isBiosimilarsReadOnly, setIsBiosimilarsReadOnly] = useState(false);
  const [selectedClinicalNoteProgram, setSelectedClinicalNoteProgram] =
    useState(null);
  const [selectedClinicalNoteType, setSelectedClinicalNoteType] = useState("");
  const [selectedClinicalAssessmentId, setSelectedClinicalAssessmentId] =
    useState(null);
  const [selectedClinicalNoteBatchId, setSelectedClinicalNoteBatchId] =
    useState(null);
  const [selectedClinicalNoteStartTime, setSelectedClinicalNoteStartTime] =
    useState(null);
  const [selectedClinicalNotes, setSelectedClinicalNotes] = useState("");
  const [isStartingClinicalNote, setIsStartingClinicalNote] = useState(false);
  const [initialAssessmentData, setInitialAssessmentData] = useState(null);
  const [clinicalNotePrograms, setClinicalNotePrograms] = useState([]);
  const [memberClinicalPrograms, setMemberClinicalPrograms] = useState([]);
  const [assessmentReferenceData, setAssessmentReferenceData] = useState(null);
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);
  const [priorAuthData, setPriorAuthData] = useState([]);
  const [isPriorAuthLoading, setIsPriorAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState({
    name: "",
    memberId: "",
    personCode: "",
    status: "Active",
    sex: "",
    gender: "",
    dateOfBirth: "",
    email: "",
    timeZone: "",
    allergies: [],
    street: "",
    state: "",
    benefitStartDate: "", // Initialize as empty
    dedIndAmt: null,
    dedFamAmt: null,
    individualOopTotal: null,
    individualMaxAmt: null,
    individualDedTotal: null,
    familyOopTotal: null,
    familyMaxAmt: null,
    beneficiaryDate: "", // Added beneficiaryDate to initial state
    familyDedTotal: null,
    pcaDirective: "",
    pcaDirectiveEnabled: false,
    copayBin: "",
    copayPcn: "",
    copayGrpId: "",
    copayMemberId: "",
    copayExpirationDate: "",
    copayExecutionStatus: "",
  });

  const hasCopayData = !!(
    memberData.copayBin &&
    memberData.copayBin !== "-" &&
    memberData.copayBin !== "N/A"
  );

  useEffect(() => {
    if (
      location.state?.fromAllMembers ||
      location.state?.source === "all-members"
    ) {
      try {
        localStorage.setItem("nav_activeItem", JSON.stringify("Members"));
        localStorage.setItem(
          "nav_activeSubItem",
          JSON.stringify("All Members"),
        );
        localStorage.setItem(
          "nav_openSubmenus",
          JSON.stringify({ Members: true }),
        );
      } catch (e) {
        return `localStorage setItem failed: ${e.message}`;
      }

      globalThis.dispatchEvent(new Event("updateNavFromProfileOpportunities"));
    }
  }, [location.state]);
  const [hasFetchedOop, setHasFetchedOop] = useState(false);
  const [hasFetchedOutcomes, setHasFetchedOutcomes] = useState(false);
  const isFetchingOutcomesRef = useRef(false);
  const isFetchingOopRef = useRef(false);
  const lastFetchedEscalationsIdRef = useRef(null);
  const [isNotesTabActive, setIsNotesTabActive] = useState(false);
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [isOutreachHistoryLoading, setIsOutreachHistoryLoading] =
    useState(false);

  /**
   * Fetches member data from the API and updates the component state
   * @async
   * @returns {Promise<void>}
   */
  const fetchMemberData = useCallback(
    async (freshData = null) => {
      if (!memberId) return;

      if (
        freshData &&
        typeof freshData === "object" &&
        (freshData.name || freshData.memberId || freshData.firstName)
      ) {
        setMemberData((prev) => {
          const checkVal = (val, prevVal) => {
            if (val && val !== "-" && val !== "N/A") return val;
            return prevVal || "";
          };
          return {
            ...prev,
            ...freshData,
            copayBin: checkVal(freshData.copayBin, prev.copayBin),
            copayPcn: checkVal(freshData.copayPcn, prev.copayPcn),
            copayGrpId: checkVal(freshData.copayGrpId, prev.copayGrpId),
            copayMemberId: checkVal(
              freshData.copayMemberId,
              prev.copayMemberId,
            ),
            copayExpirationDate: checkVal(
              freshData.copayExpirationDate,
              prev.copayExpirationDate,
            ),
            copayExecutionStatus: checkVal(
              freshData.copayExecutionStatus,
              prev.copayExecutionStatus,
            ),
          };
        });
        return;
      }

      try {
        setLoading(true);
        const data = await fetchMemberOverview(memberId);

        setMemberData((prev) => {
          const checkVal = (val, prevVal) => {
            if (val && val !== "-" && val !== "N/A") return val;
            return prevVal || "";
          };
          return {
            ...prev,
            ...data,
            copayBin: checkVal(data.copayBin, prev.copayBin),
            copayPcn: checkVal(data.copayPcn, prev.copayPcn),
            copayGrpId: checkVal(data.copayGrpId, prev.copayGrpId),
            copayMemberId: checkVal(data.copayMemberId, prev.copayMemberId),
            copayExpirationDate: checkVal(
              data.copayExpirationDate,
              prev.copayExpirationDate,
            ),
            copayExecutionStatus: checkVal(
              data.copayExecutionStatus,
              prev.copayExecutionStatus,
            ),
          };
        });
      } catch (error) {
        console.error("Unexpected error", error);
      } finally {
        setLoading(false);
      }
    },
    [memberId],
  );

  const fetchOutreachData = useCallback(async () => {
    try {
      const data = await getOutcomesOutreach();
      if (data?.outcomes && data?.outreachMethods) {
        setOutreachData(data);
      }
    } catch (error) {
      console.error("Failed to fetch outreach data, using mock data", error);
    }
  }, []);

  const fetchCopayDetails = useCallback(async () => {
    if (copayOpportunities && copayOpportunities.length > 1) {
      await Promise.all(
        copayOpportunities.map((opp) =>
          fetchCopayDetailForOpportunity(opp, setCopayDetailsMap),
        ),
      );
    }

    const oppId =
      copayOpportunity?.programId ||
      copayOpportunity?.memberProgramId ||
      copayOpportunity?.opportunityId ||
      copayOpportunity?.id;
    if (!oppId) return;

    // Check if this opportunity was already fetched in the loop above
    const alreadyFetched =
      copayOpportunities &&
      copayOpportunities.length > 1 &&
      copayOpportunities.some((opp) => {
        return getCopayOppId(opp) === oppId;
      });
    if (alreadyFetched) return;

    try {
      const copayData = await requestCopayCard(oppId);
      if (copayData) {
        const mappedData = mapCopayData(copayData);
        setMemberData((prev) => ({
          ...prev,
          ...mappedData,
        }));
        setCopayDetailsMap((prev) => ({
          ...prev,
          [oppId]: mappedData,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch copay details status:", err);
      setMemberData((prev) => ({
        ...prev,
        copayExecutionStatus: "FAILED",
      }));
      setCopayDetailsMap((prev) => ({
        ...prev,
        [oppId]: {
          copayExecutionStatus: "FAILED",
        },
      }));
    }
  }, [copayOpportunity, copayOpportunities]);

  useEffect(() => {
    fetchCopayDetails().catch((error) => {
      console.error("Failed to fetch copay details", error);
    });
  }, [fetchCopayDetails]);

  useEffect(() => {
    if (
      loading ||
      isOpportunitiesLoading ||
      isMedicationHistoryLoading ||
      isPriorAuthLoading ||
      isNotesLoading ||
      isOutreachHistoryLoading
    ) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [
    loading,
    isOpportunitiesLoading,
    isMedicationHistoryLoading,
    isPriorAuthLoading,
    isNotesLoading,
    isOutreachHistoryLoading,
    showLoading,
    hideLoading,
  ]);

  /**
   * Fetches notes for the current member with optional filtering
   * @async
   * @returns {Promise<void>}
   */
  const fetchMemberNotes = useCallback(
    async () => {
      if (!memberId) return;

      try {
        setIsNotesLoading(true);
        const entityIdToUse = /^\d+$/.test(memberId)
          ? memberId
          : memberData.memberId;

        if (!/^\d+$/.test(memberId) && !entityIdToUse) {
          setIsNotesLoading(false);
          return;
        }

        const response = await fetchNotes(entityType, entityIdToUse, {});

        let notesList = (response.content || []).filter((note) => {
          const hasContent = note.content?.trim();

          const hasNoteEntry = note.noteEntry?.trim();
          return hasContent || hasNoteEntry;
        });

        const formattedNotes = notesList.map((note) => ({
          id: note.id,
          type: note.type,
          content: note.content || note.noteEntry || "",
          details: note.content || note.noteEntry || "",
          author: note.createdBy?.toString() || "1",
          pinNote: note.pinned,
          rating: note.rating,
          dispositionStatus: note.dispositionStatus || "",
          programName: note.programName,
          createdAt: note.createdAt,
          updateAt: note.updateAt || note.updatedAt,
          date: note.createdAt,
          status: note.status,
        }));

        setNotes(formattedNotes);
      } catch (error) {
        console.error("Unexpected error", error);
      } finally {
        setIsNotesLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberId, memberData.memberId],
  );

  /**
   * Handles sequence of fetching Outcomes, OOP, and Notes
   * after the initial 3 APIs complete.
   */
  useEffect(() => {
    if (loading || isOpportunitiesLoading || isMedicationHistoryLoading) return;

    if (!hasFetchedOutcomes && !isFetchingOutcomesRef.current) {
      isFetchingOutcomesRef.current = true;
      const fetchOutcomes = async () => {
        try {
          await fetchOutreachData();
        } catch (err) {
          console.error("Failed to fetch outcomes outreach", err);
        } finally {
          setHasFetchedOutcomes(true);
        }
      };
      void fetchOutcomes();
      return;
    }

    if (!hasFetchedOop && !isFetchingOopRef.current) {
      isFetchingOopRef.current = true;
      const fetchOop = async () => {
        try {
          const today = new Date().toISOString().split("T")[0];
          const oopData = await fetchOopDetails(memberId, today);
          if (oopData) {
            setMemberData((prev) => ({
              ...prev,
              benefitStartDate: oopData.benYear ?? prev.benefitStartDate, // Use benYear from API
              dedIndAmt: oopData.dedIndAmt ?? null,
              dedFamAmt: oopData.dedFamAmt ?? null,
              individualOopTotal: oopData.oopIndTotal ?? null,
              individualMaxAmt: oopData.maxIndAmt ?? null,
              individualDedTotal: oopData.dedIndTotal ?? null,
              familyOopTotal: oopData.oopFamTotal ?? null,
              familyMaxAmt: oopData.maxFamAmt ?? null,
              familyDedTotal: oopData.dedFamTotal ?? null,
            }));
          }
        } catch (error) {
          console.error("Unexpected error fetching OOP", error);
        } finally {
          setHasFetchedOop(true);
        }
      };
      void fetchOop();
    }
  }, [
    loading,
    isOpportunitiesLoading,
    isMedicationHistoryLoading,
    hasFetchedOop,
    hasFetchedOutcomes,
    memberId,
    fetchOutreachData,
  ]);

  useEffect(() => {
    const getPriorAuthData = async () => {
      if (!memberId) return;
      setIsPriorAuthLoading(true);
      try {
        const data = await fetchPriorAuths(memberId);
        console.log(data);
        setPriorAuthData(Array.isArray(data.pas) ? data.pas : []);
      } catch (error) {
        console.error("Failed to fetch prior auth data:", error);
        setPriorAuthData([]); // Clear data on error
      } finally {
        setIsPriorAuthLoading(false);
      }
    };
    void getPriorAuthData();
  }, [memberId]);

  useEffect(() => {
    const getFamilyMembers = async () => {
      const subscriberNum = memberData?.subscriberNum;
      if (!subscriberNum) {
        setFamilyMembers([]);
        return;
      }
      setIsFamilyMembersLoading(true);
      try {
        const [data, outreachFamily] = await Promise.all([
          searchSubscribers({ subscriberNum }).catch((error) => {
            console.error("Failed to search subscribers:", error);
            return [];
          }),
          fetchFamilyBySubscriber(subscriberNum).catch((error) => {
            console.error("Failed to fetch family from outreach:", error);
            return [];
          }),
        ]);
        const results = Array.isArray(data) ? data : [];
        // Subscriber search has the fuller records; the outreach list adds
        // relCode (relationship) and anyone the subscriber search missed.
        // Person codes can come back as "01" in one source and 1 in the
        // other - compare them numerically, falling back to the name.
        const samePerson = (a, b) => {
          const codeA = Number.parseInt(a.personCode, 10);
          const codeB = Number.parseInt(b.personCode, 10);
          if (!Number.isNaN(codeA) && !Number.isNaN(codeB)) {
            return codeA === codeB;
          }
          return (
            `${a.firstName} ${a.lastName}`.trim().toLowerCase() ===
            `${b.firstName} ${b.lastName}`.trim().toLowerCase()
          );
        };
        // A relCode of null/0 means "not specified" - don't let that mask a
        // real one the other source has.
        const isRealRelCode = (v) => Number.parseInt(v, 10) > 0;
        const merged = results.map((m) => {
          const match = outreachFamily.find((o) => samePerson(o, m));
          if (!match) return m;
          return {
            ...m,
            relCode: isRealRelCode(m.relCode) ? m.relCode : match.relCode,
            memberId: m.memberId ?? match.memberId,
          };
        });
        outreachFamily.forEach((o) => {
          if (!merged.some((m) => samePerson(o, m))) merged.push(o);
        });
        setFamilyMembers(merged);
      } catch (error) {
        console.error("Failed to fetch family members:", error);
        setFamilyMembers([]);
      } finally {
        setIsFamilyMembersLoading(false);
      }
    };
    void getFamilyMembers();
  }, [memberData?.subscriberNum]);

  // Fetch notes when Notes tab is active
  useEffect(() => {
    if (isNotesTabActive) {
      void fetchMemberNotes();
    }
  }, [isNotesTabActive, memberId, fetchMemberNotes]);

  /**
   * Initial data fetch on component mount and when memberId changes
   * Fetches both member data and notes
   */
  useEffect(() => {
    if (memberId) {
      isFetchingOopRef.current = false;
      isFetchingOutcomesRef.current = false;
      setHasFetchedOop(false);
      setHasFetchedOutcomes(false);
      setIsNotesTabActive(false);
      setIsNotesLoading(false);
      setIsOutreachHistoryLoading(false);
      void fetchMemberData();
    }
  }, [memberId, fetchMemberData]);
  /**
   * State for member data including personal information and contact details
   * @type {Object}
   * @property {string} name - Full name of the member
   * @property {string} memberId - Unique identifier for the member
   * @property {string} personCode - Person code identifier
   * @property {string} status - Member status (e.g., 'Active')
   * @property {string} sex - Biological sex of the member
   * @property {string} gender - Gender identity of the member
   * @property {string} dateOfBirth - Member's date of birth
   * @property {string} email - Primary email address
   * @property {string} timeZone - Member's time zone
   * @property {Array} allergies - List of member's allergies
   * @property {Object} contact - Contact information object
   * @property {string} contact.phone - Contact phone number
   * @property {string} contact.email - Contact email
   * @property {string} contact.language - Preferred language
   * @property {string} contact.timeZone - Contact time zone
   * @property {string} contact.address - Full contact address
   */

  const [notes, setNotes] = useState([]);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isEscalationModalOpen, setIsEscalationModalOpen] = useState(false);
  const [isDispositionModalOpen, setIsDispositionModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [editingEscalation, setEditingEscalation] = useState(null);
  const [isEscalationHistoryOpen, setIsEscalationHistoryOpen] = useState(false);
  const [escalationVersions, setEscalationVersions] = useState([]);
  const [activateEscalationsTabKey, setActivateEscalationsTabKey] = useState(0);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [leftSectionsOpen, setLeftSectionsOpen] = useState({
    familyMembers: true,
    demographics: true,
    contact: true,
    accumulators: true,
    priorAuth: true,
    copayCard: true,
  });

  // Family Members section: every subscriber sharing this member's subscriber
  // number (each has its own person code), fetched by subscriber number.
  const [familyMembers, setFamilyMembers] = useState([]);
  const [isFamilyMembersLoading, setIsFamilyMembersLoading] = useState(false);

  const toggleLeftSection = useCallback((sectionKey) => {
    setLeftSectionsOpen((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  const isPhiToggleOn = useSelector((state) => state.toggles.phi);
  const isShowPhiEnabled = !isPhiToggleOn;
  const getDobDisplayValue = (dob) => {
    if (!dob) return "N/A";
    if (isShowPhiEnabled) return formatDate(dob);
    return maskDOB(dob, false);
  };

  const [dispositionTitle, setDispositionTitle] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const entityType = "MEMBER";

  const handleSaveContactInfo = useCallback(
    async (updatedInfo) => {
      try {
        showLoading();

        const mapPreferredContactMethodToEnum = (displayValue) => {
          if (!displayValue) return "Phone Number";
          const normalized = displayValue.toLowerCase().trim();
          if (normalized === "phone" || normalized === "phone number")
            return "Phone Number";
          if (normalized === "email" || normalized === "email address")
            return "Email Address";
          if (
            normalized === "alternate phone" ||
            normalized === "alternate phone number"
          )
            return "Alternate Phone Number";
          if (
            normalized === "alternate email" ||
            normalized === "alternate email address"
          )
            return "Alternate Email Address";
          return "Phone Number";
        };

        const contactUpdateData = {
          alternate_phone:
            updatedInfo.alternatePhoneNumber === ""
              ? null
              : updatedInfo.alternatePhoneNumber || null,

          alternate_email:
            updatedInfo.alternateEmail === ""
              ? null
              : updatedInfo.alternateEmail || null,
          preferredContactMethod: mapPreferredContactMethodToEnum(
            updatedInfo.preferredContactMethod,
          ),
          textingConsentObtained: updatedInfo.textingConsentObtained || false,
        };

        await updateMemberContactInfo(memberId, contactUpdateData);

        const personalInfoUpdate = {
          phone: updatedInfo.phoneNumber,
          email: updatedInfo.email || "",
          preferredContactMethod: mapPreferredContactMethodToEnum(
            updatedInfo.preferredContactMethod,
          ),
          allergies: updatedInfo.allergies || "",
          language: updatedInfo.language || "",
          timeZone: updatedInfo.timeZone || "",
          ...(updatedInfo.address && { address: updatedInfo.address }),
        };

        const response = await updateMemberPersonalInfo(
          memberId,
          personalInfoUpdate,
        );

        if (typeof response === "string" && response.includes("failed")) {
          throw new Error(response);
        }

        setMemberData((prev) => ({
          ...prev,
          phone: updatedInfo.phoneNumber || prev.phone,
          preferredPhoneNumber:
            updatedInfo.preferredPhoneNumber || prev.preferredPhoneNumber,
          preferredContactMethod:
            mapPreferredContactMethodToEnum(
              updatedInfo.preferredContactMethod,
            ) || prev.preferredContactMethod,
          email: updatedInfo.email || prev.email,

          alternatePhoneNum:
            updatedInfo.alternatePhoneNumber === undefined
              ? prev.alternatePhoneNum
              : updatedInfo.alternatePhoneNumber || "",
          alternatePhoneNumber:
            updatedInfo.alternatePhoneNumber === undefined
              ? prev.alternatePhoneNumber
              : updatedInfo.alternatePhoneNumber || "",

          alternateEmailAddress:
            updatedInfo.alternateEmail === undefined
              ? prev.alternateEmailAddress
              : updatedInfo.alternateEmail || "",
          alternateEmail:
            updatedInfo.alternateEmail === undefined
              ? prev.alternateEmail
              : updatedInfo.alternateEmail || "",
          language:
            updatedInfo.language === undefined
              ? prev.language
              : updatedInfo.language,
          timezone: updatedInfo.timeZone || "",

          ...(updatedInfo.allergies !== undefined && {
            allergies: (() => {
              if (Array.isArray(updatedInfo.allergies)) {
                return updatedInfo.allergies;
              } else if (updatedInfo.allergies) {
                return [updatedInfo.allergies];
              } else {
                return [];
              }
            })(),
          }),

          ...(updatedInfo.address && {
            address: {
              ...prev.address,
              fullAddress: updatedInfo.address,
              ...(typeof updatedInfo.address === "string" && {
                street: updatedInfo.address.split(",")[0]?.trim() || "",
                city: updatedInfo.address.split(",")[2]?.trim() || "",
                state: updatedInfo.address.split(",")[3]?.trim() || "",
                zip: updatedInfo.address.split(",").pop()?.trim() || "",
              }),
            },
          }),
          contact: {
            ...prev.contact,
            language:
              updatedInfo.language === undefined
                ? prev.contact?.language
                : updatedInfo.language,
          },
          textingConsentObtained: updatedInfo.textingConsentObtained || false,
        }));

        setContactModalOpen(false);
      } catch (error) {
        console.error("Unexpected error", error);
      } finally {
        hideLoading();
      }
    },
    [memberId, showLoading, hideLoading],
  );

  const handleAddDisposition = async (newDisposition) => {
    try {
      setMemberData((prev) => ({
        ...prev,
        dispositions: [
          {
            ...newDisposition,
            id: Date.now().toString(),
            author: "Current User",
            date: new Date()
              .toLocaleString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
              .replace(",", ""),
          },
          ...(prev.dispositions || []),
        ],
      }));

      setIsDispositionModalOpen(false);

      showLoading();
      await Promise.all([
        (async () => {
          setOpportunitiesReloadKey((prev) => prev + 1);
        })(),
        fetchMemberData(),
        // Best-effort: refreshes this member's outreach-list snapshot so the
        // Recent Activity column reflects this disposition without waiting
        // for the backend's scheduled cache warmup. A failure here shouldn't
        // block the rest of the save flow - the warmup will catch up eventually.
        resyncOutreachCache(memberId).catch((error) => {
          console.error("Failed to resync outreach cache", error);
        }),
      ]);
      hideLoading();
      setDispositionOpportunity(null);
      setExternalOutcomeTriggerKey(0);
    } catch (error) {
      console.error(
        "Failed to refetch data after disposition submission:",
        error,
      );
      hideLoading();
      setExternalOutcomeTriggerKey(0);
      /* Snackbar notification is currently disabled */
    }
  };

  const toggleNotePin = async (noteId) => {
    try {
      let noteToToggle =
        typeof noteId === "object"
          ? noteId
          : notes.find((note) => note.id === noteId);
      if (!noteToToggle) return;

      if (
        noteToToggle.status === "ARCHIVED" ||
        noteToToggle.status === "CREATED"
      ) {
        const newStatus =
          noteToToggle.status === "ARCHIVED" ? "CREATED" : "ARCHIVED";
        await editNoteApi(noteToToggle.id, {
          ...noteToToggle,
          status: newStatus,
        });
        await fetchMemberNotes();
      }

      if (noteToToggle.pinNote) {
        await editNoteApi(noteToToggle.id, {
          ...noteToToggle,
          pinned: false,
        });
      } else {
        const currentlyPinnedNote = notes.find((note) => note.pinNote);
        if (currentlyPinnedNote) {
          await editNoteApi(currentlyPinnedNote.id, {
            ...currentlyPinnedNote,
            pinned: false,
          });
        }

        await editNoteApi(noteToToggle.id, {
          ...noteToToggle,
          pinned: true,
        });
      }

      await fetchMemberNotes();
    } catch (error) {
      console.error("Unexpected error", error);
    } finally {
      console.log("In finally block");
    }
  };

  const handleEditNote = async (note) => {
    if (!canWriteModule) return;
    setEditingNote(note);
    setIsNoteModalOpen(true);
  };

  const handleAddNote = async (newNote) => {
    try {
      const isEdit = !!editingNote;

      if (newNote.pinNote && !isEdit) {
        const hasPinnedNote = notes.some((note) => note.pinNote);
        if (hasPinnedNote) {
          return "Please unpin the current pinned note before pinning a new one";
        }
      }

      const payload = {
        type: newNote.noteTypes?.[0] || "General",
        content: newNote.noteDetails || "",
        rating: newNote.rating || 1,
        pinned: newNote.pinNote || false,
        programId: Number(newNote.programId),
        status: newNote.status || (newNote.archived ? "ARCHIVED" : "CREATED"),
        archived: newNote.archived === undefined ? false : newNote.archived,
      };

      if (isEdit) {
        await editNoteApi(editingNote.id, payload);
      } else {
        const entityIdToUse = /^\d+$/.test(memberId)
          ? memberId
          : memberData.memberId;

        await addNoteApi(memberId, {
          ...payload,
          entityType: "MEMBER",
          entityId: String(entityIdToUse),
        });
      }

      await fetchMemberNotes();

      setIsNoteModalOpen(false);
      setEditingNote(null);
    } catch (error) {
      console.error("Unexpected error", error);
      setSnackbar({
        open: true,
        message: `Failed to save note: ${error.message}`,
        severity: "error",
      });
      setIsNoteModalOpen(false);
      setEditingNote(null);
    }
  };

  const getEscalationEntityId = useCallback(
    () => (/^\d+$/.test(memberId) ? memberId : memberData.memberId),
    [memberId, memberData.memberId],
  );

  const fetchMemberEscalations = useCallback(
    async (targetEntityId) => {
      const entityIdToUse = targetEntityId || getEscalationEntityId();
      if (!entityIdToUse) return;

      try {
        setEscalations(await fetchEscalations(entityIdToUse));
      } catch (error) {
        console.error("Failed to fetch escalations", error);
      }
    },
    [getEscalationEntityId],
  );

  useEffect(() => {
    const entityIdToUse = /^\d+$/.test(memberId)
      ? memberId
      : memberData.memberId;
    if (
      entityIdToUse &&
      lastFetchedEscalationsIdRef.current !== entityIdToUse
    ) {
      lastFetchedEscalationsIdRef.current = entityIdToUse;
      fetchMemberEscalations(entityIdToUse);
    }
  }, [memberId, memberData.memberId, fetchMemberEscalations]);

  const closeEscalationModal = () => {
    setIsEscalationModalOpen(false);
    setEditingEscalation(null);
  };

  const handleSaveEscalation = async (escalationData) => {
    const entityIdToUse = getEscalationEntityId();

    if (editingEscalation) {
      await updateEscalation(
        entityIdToUse,
        editingEscalation.id,
        escalationData,
      );
      await fetchMemberEscalations();
      closeEscalationModal();
      setIsEscalationHistoryOpen(false);
      setSnackbar({
        open: true,
        message: "Escalation updated successfully",
        severity: "success",
      });
      return;
    }

    const { pendingContactAttempts = [], ...createPayload } = escalationData;
    const created = await createEscalation(entityIdToUse, createPayload);

    // A contact attempt logged while creating can't be saved until the
    // escalation itself exists (the contact-attempts endpoint is scoped to an
    // escalation id) - so it's staged on the modal and only submitted now,
    // as a second call against the id createEscalation just returned.
    const results = await Promise.allSettled(
      pendingContactAttempts.map((attempt) =>
        createContactAttempt(entityIdToUse, created.escalationId, attempt),
      ),
    );
    const failedAttempts = results.filter((r) => r.status === "rejected");
    failedAttempts.forEach((r) => {
      console.error("Failed to save contact attempt", r.reason);
    });
    const contactAttemptFailed = failedAttempts.length > 0;

    await fetchMemberEscalations();
    closeEscalationModal();
    // Creating an escalation can be started from any tab (there's no
    // Escalations tab at all until the first one exists) - jump to it now
    // that fetchMemberEscalations has made it appear in visibleTabs.
    setActivateEscalationsTabKey((key) => key + 1);
    setSnackbar({
      open: true,
      message: contactAttemptFailed
        ? "Escalation created, but the contact attempt could not be saved."
        : "Escalation created successfully",
      severity: contactAttemptFailed ? "warning" : "success",
    });
  };

  const handleSelectEscalation = async (escalation) => {
    const entityIdToUse = getEscalationEntityId();
    try {
      setEscalationVersions(
        await fetchEscalationVersions(entityIdToUse, escalation.id, escalation),
      );
      setIsEscalationHistoryOpen(true);
    } catch (error) {
      console.error("Failed to fetch escalation history", error);
    }
  };

  const handleSelectEscalationVersion = (version) => {
    setEditingEscalation(version);
    setIsEscalationModalOpen(true);
  };

  const getBreadcrumbItems = () => {
    let items = [];
    if (fromMemberMonitoring) {
      items = [
        { label: "Members", path: "/" },
        { label: "Member Monitoring", path: "/members/monitoring" },
        {
          label: memberData?.name
            ? getNameVariants(memberData.name, isShowPhiEnabled).maskedMiddle
            : "Member Details",
          path: "",
        },
      ];
    } else if (fromPriorityOutreach || fromManualTasks) {
      items = [
        { label: "Member Outreach", path: "/members/member-outreach" },
        {
          label: memberData?.name
            ? getNameVariants(memberData.name, isShowPhiEnabled).maskedMiddle
            : "Member Details",
          path: "",
        },
      ];
    } else if (fromAllMembers) {
      items = [
        { label: "Members", path: "/" },
        { label: "All Members", path: "/members/all-members" },
        {
          label: memberData?.name
            ? getNameVariants(memberData.name, isShowPhiEnabled).maskedMiddle
            : "Member Details",
          path: "",
        },
      ];
    } else {
      items = [
        { label: "Members", path: "/" },
        {
          label: memberData?.name
            ? getNameVariants(memberData.name, isShowPhiEnabled).maskedMiddle
            : "Member Details",
          path: "",
        },
      ];
    }

    if (isClinicalNoteView) {
      items = items.map((item, index) => {
        if (index === items.length - 1) {
          return {
            ...item,
            onClick: () => {
              confirmBreadcrumbNavigation(() => {
                setSelectedClinicalAssessmentId(null);
                setSelectedClinicalNoteBatchId(null);
                setSelectedClinicalNoteStartTime(null);
                setSelectedClinicalNotes("");
                setInitialAssessmentData(null);
                setIsDraftSession(false);
                setIsClinicalNoteView(false);
              });
            },
          };
        }
        return {
          ...item,
          onClick: (e) => {
            e?.preventDefault?.();
            confirmBreadcrumbNavigation(() => {
              setSelectedClinicalAssessmentId(null);
              setSelectedClinicalNoteBatchId(null);
              setSelectedClinicalNoteStartTime(null);
              setSelectedClinicalNotes("");
              setInitialAssessmentData(null);
              setIsDraftSession(false);
              setIsClinicalNoteView(false);
              void navigate(item.path);
            });
          },
        };
      });
      items.push({ label: "Clinical Assessment", path: "" });
    }

    return items;
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      setIsLoadingPrograms(true);
      try {
        const [groups, memberProgs, refData] = await Promise.all([
          fetchAssessmentGroups({}),
          memberId
            ? fetchMemberClinicalPrograms(memberId)
            : Promise.resolve([]),
          fetchAssessmentReferenceData(),
        ]);
        const activeGroups = (groups || []).filter(
          (g) => g.status === "ACTIVE" && g.groupType !== "BIOSIMILARS",
        );
        setClinicalNotePrograms(activeGroups);
        if (Array.isArray(memberProgs)) {
          setMemberClinicalPrograms(memberProgs);
        } else if (Array.isArray(memberProgs?.content)) {
          setMemberClinicalPrograms(memberProgs.content);
        } else if (Array.isArray(memberProgs?.programs)) {
          setMemberClinicalPrograms(memberProgs.programs);
        } else if (Array.isArray(memberProgs?.data)) {
          setMemberClinicalPrograms(memberProgs.data);
        } else {
          setMemberClinicalPrograms([]);
        }
        if (refData) {
          setAssessmentReferenceData(refData);
        }
      } catch (error) {
        console.error(
          "Failed to fetch active programs for clinical note:",
          error,
        );
      } finally {
        setIsLoadingPrograms(false);
      }
    };
    void fetchPrograms();
  }, [memberId, opportunitiesReloadKey]);

  const ensureAssessmentReferenceData = async () => {
    let refData = assessmentReferenceData || getCachedAssessmentReferenceData();
    if (refData) return refData;
    try {
      refData = await fetchAssessmentReferenceData();
      if (refData) setAssessmentReferenceData(refData);
    } catch (e) {
      console.error("Failed to fetch assessment reference data on click:", e);
    }
    return refData;
  };

  const ensureMemberClinicalPrograms = async () => {
    const currentMemberProgs = memberClinicalPrograms || [];
    if (currentMemberProgs.length > 0 || !memberId) return currentMemberProgs;
    try {
      const fetched = await fetchMemberClinicalPrograms(memberId);
      const fetchedList = extractProgramList(fetched);
      if (fetchedList.length > 0) {
        setMemberClinicalPrograms(fetchedList);
        return fetchedList;
      }
    } catch (e) {
      // ignore
    }
    return currentMemberProgs;
  };

  const resumeClinicalNoteDraft = (draftInfo) => {
    const candidateAssessmentId = getDraftAssessmentId(draftInfo);

    setSelectedClinicalAssessmentId(candidateAssessmentId);
    setSelectedClinicalNoteBatchId(
      draftInfo.batchId || (draftInfo.isBatch ? candidateAssessmentId : null),
    );
    setInitialAssessmentData(null);
    const programId = draftInfo.programId;
    const programName = draftInfo.category;
    const matchedProg = clinicalNotePrograms.find(
      (p) => p.programId === programId || p.groupName === programName,
    ) || {
      groupName: programName || "General",
      programId: programId || "OTHER",
      groupType:
        programId === "OTHER" || !programId ? "GENERAL" : "PROGRAM_SPECIFIC",
    };
    setSelectedClinicalNoteProgram(matchedProg);
    setSelectedClinicalNoteType(draftInfo.assessmentType || "INITIAL");
    setIsClinicalNoteReadOnly(false);
    setIsClinicalNoteView(true);
  };

  const handleStartClinicalNoteClick = async (
    draftInfo,
    isOnlyBiosimilars = false,
  ) => {
    const refData = await ensureAssessmentReferenceData();
    const currentMemberProgs = await ensureMemberClinicalPrograms();

    const rawActiveMemberProgs = (currentMemberProgs || []).filter((mp) => {
      const progId = String(mp.programId || mp.id || "").toUpperCase();
      const gType = String(mp.groupType || mp.type || "").toUpperCase();
      return (
        gType !== "GENERAL" &&
        gType !== "OTHER" &&
        progId !== "GENERAL" &&
        progId !== "OTHER"
      );
    });

    const isOnlyBiosimilarsProgram =
      Boolean(isOnlyBiosimilars) ||
      (rawActiveMemberProgs.length > 0 &&
        rawActiveMemberProgs.every((mp) => checkBiosimilar(mp))) ||
      (rawActiveMemberProgs.length === 0 &&
        Object.keys(programMap || {}).length > 0 &&
        Object.entries(programMap || {}).every(
          ([id, name]) => checkBiosimilar(id) || checkBiosimilar(name),
        ));

    // Check if this click is explicitly resuming an existing draft
    const hasExplicitDraft = Boolean(getDraftAssessmentId(draftInfo));
    setIsDraftSession(hasExplicitDraft);

    if (!hasExplicitDraft && isOnlyBiosimilarsProgram) {
      setSelectedBiosimilarsNote(null);
      setIsBiosimilarsReadOnly(false);
      setIsBiosimilarsOutcomeOpen(true);
      return;
    }

    // Exclude biosimilars completely from clinical note assessment workflow
    const activeMemberProgs = rawActiveMemberProgs.filter(
      (mp) => !checkBiosimilar(mp),
    );

    const formlessProgramMapEntries =
      Object.keys(programMap || {}).length > 0
        ? Object.entries(programMap || {}).filter(
            ([id, name]) =>
              !checkBiosimilar(id) &&
              !checkBiosimilar(name) &&
              checkIsFormless({ id, name }, refData),
          )
        : [];

    const nonBiosimilarProgramMapEntries =
      Object.keys(programMap || {}).length > 0
        ? Object.entries(programMap || {}).filter(
            ([id, name]) => !checkBiosimilar(id) && !checkBiosimilar(name),
          )
        : [];

    const isMemberOnlyFormless =
      (activeMemberProgs.length > 0 &&
        activeMemberProgs.every((mp) => checkIsFormless(mp, refData))) ||
      (activeMemberProgs.length === 0 &&
        formlessProgramMapEntries.length > 0 &&
        formlessProgramMapEntries.length ===
          nonBiosimilarProgramMapEntries.length);

    if (hasExplicitDraft) {
      resumeClinicalNoteDraft(draftInfo);
      return;
    }

    // User clicked "Start Clinical Assessment" / "Start Clinical Note":
    // If user has only formless programs then directly open the Eligible Programs section Without calling the create assessment API
    if (isMemberOnlyFormless) {
      setSelectedClinicalAssessmentId(null);
      setSelectedClinicalNoteBatchId(null);
      setInitialAssessmentData(null);
      setIsClinicalNotePopupOpen(false);
      const formlessProgs = (activeMemberProgs || []).filter(
        (p) => checkIsFormless(p, refData) && !checkBiosimilar(p),
      );
      let formlessFallback = (clinicalNotePrograms || []).filter(
        (p) => !checkBiosimilar(p),
      );
      if (activeMemberProgs.length > 0) {
        formlessFallback = activeMemberProgs;
      } else if (formlessProgramMapEntries.length > 0) {
        formlessFallback = formlessProgramMapEntries.map(([id, name]) => ({
          id,
          name,
          programId: id,
          programName: name,
          programDisplayName: name,
          groupName: name,
          formless: true,
          isFormless: true,
        }));
      }
      setSelectedClinicalNoteProgram(
        formlessProgs.length > 0 ? formlessProgs : formlessFallback,
      );
      setSelectedClinicalNoteType("");
      setIsClinicalNoteReadOnly(false);
      setIsClinicalNoteView(true);
      return;
    }

    // Show the Start Clinical Assessment modal to choose Assessment Type and view mapped programs
    setSelectedClinicalAssessmentId(null);
    setSelectedClinicalNoteBatchId(null);
    setInitialAssessmentData(null);
    setIsClinicalNotePopupOpen(true);
  };

  const handleClinicalNoteNext = async (programs, type) => {
    setIsStartingClinicalNote(true);
    setIsDraftSession(false);
    try {
      const refData =
        assessmentReferenceData || getCachedAssessmentReferenceData();

      const validPrograms = (programs || []).filter(
        (p) =>
          !checkBiosimilar(p) &&
          !checkBiosimilar(p?.id) &&
          !checkBiosimilar(p?.name) &&
          !isNonClinicalProgram(p),
      );

      const isAllFormless =
        validPrograms &&
        validPrograms.length > 0 &&
        validPrograms.every((p) => p.isFormless || checkIsFormless(p, refData));

      if (isAllFormless) {
        setSelectedClinicalAssessmentId(null);
        setSelectedClinicalNoteBatchId(null);
        setSelectedClinicalNoteProgram(validPrograms);
        setSelectedClinicalNoteType("");
        setInitialAssessmentData(null);
        setIsClinicalNotePopupOpen(false);
        setIsClinicalNoteReadOnly(false);
        setIsClinicalNoteView(true);
        return;
      }
      const parsedMemberId = Number.parseInt(
        memberId || memberData?.memberId || memberData?.id,
        10,
      );
      const defaultType = String(type || "INITIAL").toUpperCase();
      const initialPrograms = validPrograms.map((prog) => {
        const rawId = prog.programId || prog.id || prog.name;
        const isFormless = Boolean(
          prog.isFormless || checkIsFormless(prog, refData),
        );
        const clean = cleanProgramId(rawId);
        const rawType = prog.formType || prog.assessmentType;
        const progType =
          isFormless ||
          !rawType ||
          String(rawType).trim() === "" ||
          String(rawType).toUpperCase() === "N/A"
            ? "N/A"
            : String(rawType || defaultType || "INITIAL").toUpperCase();
        return {
          programId: clean || String(rawId),
          formType: isFormless ? "N/A" : progType,
          ...(prog.manuallyAdded ? { manuallyAdded: true } : {}),
        };
      });

      const payload = {
        memberId: parsedMemberId,
        encounterId: null,
        programs: initialPrograms,
      };

      console.log(
        "Start Clinical Assessment modal Next: initializing assessment with payload:",
        payload,
      );
      const res = await initializeAssessmentApi(payload);
      console.log("Assessment initialized successfully:", res);

      const createdId = res?.id || null;
      const visitType = res?.assessmentType;

      const mergedPrograms = validPrograms.map((vp) => {
        const rawId = vp.programId || vp.id || vp.name;
        const clean = cleanProgramId(rawId);
        const serverProg = Array.isArray(res?.programs)
          ? res.programs.find((rp) => {
              const rpId =
                typeof rp === "object" ? rp.programId || rp.id || rp.code : rp;
              return (
                rpId === rawId ||
                cleanProgramId(rpId) === clean ||
                String(rpId).toUpperCase() === String(rawId).toUpperCase()
              );
            })
          : null;
        const fType =
          vp.formType ||
          vp.assessmentType ||
          (typeof serverProg === "object" ? serverProg?.formType : undefined);
        const aType =
          vp.assessmentType ||
          vp.formType ||
          (typeof serverProg === "object"
            ? serverProg?.assessmentType
            : undefined);
        return {
          ...(typeof serverProg === "object" ? serverProg : {}),
          ...vp,
          formType: fType,
          assessmentType: aType,
          initialFormType: (fType || aType || "INITIAL").toUpperCase(),
          defaultAssessmentType: (fType || aType || "INITIAL").toUpperCase(),
        };
      });

      const mergedRes = res
        ? {
            ...res,
            programs:
              Array.isArray(res.programs) && res.programs.length > 0
                ? res.programs.map((rp) => {
                    const rawId =
                      typeof rp === "object"
                        ? rp.programId || rp.id || rp.code
                        : rp;
                    const clean = cleanProgramId(rawId);
                    const matchedVp = mergedPrograms.find((vp) => {
                      const vpId = vp.programId || vp.id;
                      return (
                        vpId === rawId ||
                        cleanProgramId(vpId) === clean ||
                        String(vpId).toUpperCase() ===
                          String(rawId).toUpperCase()
                      );
                    });
                    const finalType =
                      matchedVp?.formType ||
                      matchedVp?.assessmentType ||
                      (typeof rp === "object" &&
                        (rp.formType || rp.assessmentType));
                    return {
                      ...(typeof rp === "object" ? rp : { programId: rp }),
                      formType: finalType,
                      assessmentType: finalType,
                    };
                  })
                : mergedPrograms,
          }
        : res;

      setSelectedClinicalAssessmentId(createdId);
      setSelectedClinicalNoteBatchId(null);
      setSelectedClinicalNoteProgram(mergedPrograms);
      setSelectedClinicalNoteType(visitType);
      setInitialAssessmentData(mergedRes);
      setIsClinicalNotePopupOpen(false);
      setIsClinicalNoteReadOnly(false);
      setIsClinicalNoteView(true);
    } catch (err) {
      console.error("Failed to initialize clinical assessment:", err);
      const validPrograms = (programs || []).filter(
        (p) =>
          !checkBiosimilar(p) &&
          !checkBiosimilar(p?.id) &&
          !checkBiosimilar(p?.name),
      );
      const fallbackPrograms = validPrograms.map((vp) => ({
        ...vp,
        formType: vp.formType || vp.assessmentType || "INITIAL",
        assessmentType: vp.assessmentType || vp.formType || "INITIAL",
        initialFormType: (
          vp.formType ||
          vp.assessmentType ||
          "INITIAL"
        ).toUpperCase(),
        defaultAssessmentType: (
          vp.formType ||
          vp.assessmentType ||
          "INITIAL"
        ).toUpperCase(),
      }));
      setSelectedClinicalAssessmentId(null);
      setSelectedClinicalNoteBatchId(null);
      setSelectedClinicalNoteProgram(fallbackPrograms);
      setSelectedClinicalNoteType(String(type || "INITIAL").toUpperCase());
      setInitialAssessmentData(null);
      setIsClinicalNotePopupOpen(false);
      setIsClinicalNoteReadOnly(false);
      setIsClinicalNoteView(true);
    } finally {
      setIsStartingClinicalNote(false);
    }
  };

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  const pageRootRef = useRef(null);
  const containerRef = useRef(null);
  const [computedHeight, setComputedHeight] = useState(0);

  useEffect(() => {
    const computeHeight = () => {
      const el = containerRef.current;
      if (!el) return;
      const vh =
        globalThis.window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;

      const isSmallLaptop =
        globalThis.window.innerWidth <= 1440 &&
        globalThis.window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      const h = Math.max(200, Math.floor(vh - top - bottomPadding + extra));
      setComputedHeight(h);
    };

    computeHeight();
    globalThis.window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      if (document.body) ro.observe(document.body);
    } catch (error) {
      console.debug("ResizeObserver loop limit exceeded:", error);
    }
    return () => {
      globalThis.window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, [isClinicalNoteView]);

  const handleReopenClinicalNote = (note, readOnly) => {
    const checkBiosimilar = (val) => {
      if (!val) return false;
      if (typeof val === "string")
        return val.toUpperCase().includes("BIOSIMILAR");
      if (typeof val === "object") {
        return (
          checkBiosimilar(val.programId) ||
          checkBiosimilar(val.programName) ||
          checkBiosimilar(val.name) ||
          checkBiosimilar(val.program) ||
          checkBiosimilar(val.id) ||
          checkBiosimilar(val.canonicalId)
        );
      }
      return false;
    };

    const bioProg = Array.isArray(note?.programs)
      ? note.programs.find((p) => checkBiosimilar(p))
      : null;

    const isBiosimilarsNote =
      !!bioProg ||
      checkBiosimilar(note?.programId) ||
      checkBiosimilar(note?.programName) ||
      checkBiosimilar(note?.program);

    if (isBiosimilarsNote) {
      setSelectedBiosimilarsNote(note);
      setIsBiosimilarsReadOnly(!!readOnly);
      setIsBiosimilarsOutcomeOpen(true);
      return;
    }

    // readOnly=true  → submitted note (view only)
    // readOnly=false → draft note (reopen for editing)
    setIsClinicalNoteReadOnly(!!readOnly);
    const isDraftItem =
      !readOnly &&
      (String(note?.status || "").toUpperCase() === "DRAFT" ||
        note?.isDraft === true);
    setIsDraftSession(Boolean(isDraftItem));
    if (note) {
      const programName = Array.isArray(note.programs)
        ? note.programs[0]
        : note.programName || note.program;

      const matchedProg = clinicalNotePrograms.find(
        (p) => p.groupName === programName || p.programId === note.programId,
      ) || {
        groupName: programName || "General",
        programId: note.programId || "OTHER",
        groupType: note.programId === "OTHER" ? "GENERAL" : "PROGRAM_SPECIFIC",
      };

      setSelectedClinicalNoteProgram(matchedProg);
      setSelectedClinicalNoteType(
        note.assessmentType || note.type || "initial",
      );
      setSelectedClinicalAssessmentId(note.id);
      setSelectedClinicalNoteBatchId(
        note.batchId || (note.isBatch ? note.id : null),
      );
      setSelectedClinicalNoteStartTime(note.startTime || null);
      const noteText =
        note?.clinicalNote ||
        note?.clinicalNotes ||
        note?.note ||
        note?.notes ||
        note?.narrative ||
        note?.summary ||
        "";
      setSelectedClinicalNotes(noteText);
    }
    setIsClinicalNoteView(true);
  };

  useEffect(() => {
    const root = pageRootRef.current;
    if (!root) return;

    const selector =
      'div[data-mui-internal-clone-element="true"][aria-label],span[data-mui-internal-clone-element="true"][aria-label]';

    const clean = () => {
      const nodes = root.querySelectorAll(selector);
      for (const node of nodes) {
        const label = node.getAttribute("aria-label");
        if (!label) continue;
        if (!node.getAttribute("title")) node.setAttribute("title", label);
        node.removeAttribute("aria-label");
      }
    };

    clean();
    const mo = new MutationObserver((mutations) => {
      let needsClean = false;
      for (const m of mutations) {
        if (m.type === "childList") {
          needsClean = true;
          break;
        }
        if (
          m.type === "attributes" &&
          m.attributeName === "aria-label" &&
          (m.target?.matches?.(selector) || true)
        ) {
          needsClean = true;
          break;
        }
      }
      if (needsClean) clean();
    });
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label"],
    });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const root = pageRootRef.current;
    if (!root) return;

    const isElementScrollable = (el) => {
      const cs = globalThis.getComputedStyle(el);
      const overflowY = cs.overflowY;
      const overflow = cs.overflow;
      const mayScroll =
        overflowY === "auto" ||
        overflowY === "scroll" ||
        overflow === "auto" ||
        overflow === "scroll";
      if (!mayScroll) return false;
      return el.scrollHeight > el.clientHeight + 1;
    };

    const ensureFocusableScrollRegions = () => {
      const nodes = root.querySelectorAll("*");
      for (const el of nodes) {
        if (!isElementScrollable(el)) continue;

        if (
          el.matches(
            'a[href], button, input, select, textarea, [contenteditable="true"]',
          )
        )
          continue;
        const hasTab = el.hasAttribute("tabindex");
        const ti = hasTab
          ? Number.parseInt(el.getAttribute("tabindex") || "-1", 10)
          : -1;
        if (ti >= 0) continue;
        el.setAttribute("tabindex", "0");
      }
    };

    ensureFocusableScrollRegions();
    const mo = new MutationObserver(() => {
      ensureFocusableScrollRegions();
    });
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    return () => mo.disconnect();
  }, []);

  const memberInitials = useMemo(() => {
    const fullName = String(memberData.name || "").trim();
    if (!fullName) return "NA";
    const parts = fullName.split(/\s+/).filter(Boolean);
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");
  }, [memberData.name]);

  const displayName = useMemo(() => {
    return memberData.name
      ? getNameVariants(memberData.name, isShowPhiEnabled).maskedMiddle
      : "";
  }, [memberData.name, isShowPhiEnabled]);

  const headerContainerRef = useRef(null);
  const nameRef = useRef(null);
  const chipsRef = useRef(null);
  const [wrapChipsBelow, setWrapChipsBelow] = useState(false);

  useEffect(() => {
    const checkFit = () => {
      if (headerContainerRef.current && nameRef.current && chipsRef.current) {
        const containerWidth = headerContainerRef.current.clientWidth;
        const nameWidth = nameRef.current.scrollWidth;
        const chipsWidth = chipsRef.current.scrollWidth;
        const needsWrap = nameWidth + chipsWidth + 16 > containerWidth;
        setWrapChipsBelow(needsWrap);
      }
    };

    checkFit();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && headerContainerRef.current) {
      resizeObserver = new ResizeObserver(checkFit);
      resizeObserver.observe(headerContainerRef.current);
    }

    window.addEventListener("resize", checkFit);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", checkFit);
    };
  }, [displayName, memberData?.status, memberData?.dispositionStatus]);

  // Other subscribers sharing this member's subscriber number, excluding the
  // member currently being viewed (matched by person code, falling back to
  // member id). The Family Members section is only shown when this is non-empty.
  const otherFamilyMembers = useMemo(() => {
    const selfPersonCode = memberData?.personCode;
    const selfMemberId = memberData?.memberId;
    return familyMembers.filter((m) => {
      const sameByPersonCode =
        selfPersonCode != null &&
        Number.parseInt(m.personCode, 10) ===
          Number.parseInt(selfPersonCode, 10);
      const sameByMemberId =
        selfMemberId != null &&
        m.memberId != null &&
        String(m.memberId) === String(selfMemberId);
      return !sameByPersonCode && !sameByMemberId;
    });
  }, [familyMembers, memberData?.personCode, memberData?.memberId]);

  // While the family is still loading the section shows a spinner instead of
  // staying hidden, so it does not pop in later.
  const showFamilyMembersSection =
    isFamilyMembersLoading || otherFamilyMembers.length > 0;

  const maskedAddressText = useMemo(() => {
    const addr = memberData.address || {};
    const maskedLine1 = addr.line1
      ? maskAddressLine(addr.line1, isShowPhiEnabled)
      : null;
    const maskedLine2 = addr.line2
      ? maskAddressLine(addr.line2, isShowPhiEnabled)
      : null;
    const maskedCity = addr.city ? maskCity(addr.city, isShowPhiEnabled) : null;
    const maskedState = addr.state
      ? maskState(addr.state, isShowPhiEnabled)
      : null;
    const maskedPostalCode = addr.postalCode
      ? maskPostalCode(addr.postalCode, isShowPhiEnabled)
      : null;

    const addressFields = [
      maskedLine1,
      maskedLine2,
      [maskedCity, maskedState, maskedPostalCode].filter(Boolean).join(" "),
    ].filter(Boolean);

    if (
      !memberData.address ||
      addressFields.every((f) => !f || f.trim() === "")
    ) {
      return "N/A";
    }

    return addressFields.join(", ");
  }, [memberData.address, isShowPhiEnabled]);

  const knownAllergiesDisplay = useMemo(() => {
    let rawAllergies = memberData.knownAllergies;
    if (rawAllergies == null) {
      rawAllergies = memberData.allergies;
    }
    if (rawAllergies == null) {
      rawAllergies = memberData.allergy;
    }

    let allergiesList = [];
    if (Array.isArray(rawAllergies)) {
      allergiesList = rawAllergies;
    } else if (rawAllergies) {
      allergiesList = [rawAllergies];
    }

    const flattenedAllergies = allergiesList
      .flatMap((allergy) =>
        String(allergy)
          .split(",")
          .map((item) => item.trim()),
      )
      .filter(Boolean);

    if (flattenedAllergies.length === 0) {
      return { text: "N/A", shouldTruncate: false };
    }

    const maskedAllergies = flattenedAllergies
      .map((allergy) => {
        const words = allergy.split(/\s+/).filter(Boolean);
        if (words.length === 0) return "";

        const maskedWords = words.map((word, index) => {
          if (index === 0) {
            return maskShowInitialOnly(word, isShowPhiEnabled);
          }
          return maskAll(word, isShowPhiEnabled);
        });

        return maskedWords.join(" ");
      })
      .filter(Boolean);

    const text = maskedAllergies.join(", ");

    return {
      text: text.length > 20 ? `${text.slice(0, 20)}...` : text,
      shouldTruncate: text.length > 20,
      rawAllergies,
    };
  }, [
    memberData.allergy,
    memberData.allergies,
    memberData.knownAllergies,
    isShowPhiEnabled,
  ]);

  const accumulatorRows = useMemo(() => {
    const toAmount = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
    };

    const buildRow = (label, current, max, color) => {
      if (current == null && max == null) {
        return {
          label,
          valueText: "N/A",
          color,
          pct: 0,
        };
      }

      const safeCurrent = toAmount(current);
      const safeMax = toAmount(max);
      const pct =
        safeMax > 0 ? Math.min((safeCurrent / safeMax) * 100, 100) : 0;

      return {
        label,
        valueText: `${formatDecimalNumber(safeCurrent)} / ${formatDecimalNumber(safeMax)}`,
        color,
        pct,
      };
    };

    return [
      buildRow(
        "Individual Deductible",
        memberData.individualDedTotal,
        memberData.dedIndAmt,
        "#5E55CC",
      ),
      buildRow(
        "Out-of-Pocket Individual",
        memberData.individualOopTotal,
        memberData.individualMaxAmt,
        "#D89B19",
      ),
      buildRow(
        "Family Deductible",
        memberData.familyDedTotal,
        memberData.dedFamAmt,
        "#59B33E",
      ),
      buildRow(
        "Out-of-Pocket Family",
        memberData.familyOopTotal,
        memberData.familyMaxAmt,
        "#D13D53",
      ),
    ];
  }, [
    memberData.dedIndAmt,
    memberData.dedFamAmt,
    memberData.individualDedTotal,
    memberData.individualMaxAmt,
    memberData.individualOopTotal,
    memberData.familyDedTotal,
    memberData.familyMaxAmt,
    memberData.familyOopTotal,
  ]);

  const clinicalNoteOpportunityProgram = useMemo(() => {
    const oppIds = Object.keys(programMap || {});
    if (oppIds.length === 0) return null;
    const found = clinicalNotePrograms.find((p) => {
      const hasIdMatch =
        p.programId &&
        oppIds.some(
          (oppId) => oppId.toLowerCase() === p.programId.toLowerCase(),
        );
      const hasNameMatch =
        p.groupName &&
        Object.values(programMap).some((name) => {
          const normalizedName = name
            .toLowerCase()
            .replaceAll("program", "")
            .trim();
          const normalizedGroupName = p.groupName
            .toLowerCase()
            .replaceAll("program", "")
            .trim();
          return (
            normalizedName === normalizedGroupName ||
            normalizedName.includes(normalizedGroupName) ||
            normalizedGroupName.includes(normalizedName)
          );
        });
      return hasIdMatch || hasNameMatch;
    });
    return found || null;
  }, [programMap, clinicalNotePrograms]);

  const getPriorAuthActionLetter = (action) => {
    const normalized = String(action || "").toLowerCase();
    if (normalized === "approved") return "A";
    if (normalized === "denied") return "D";

    return "";
  };

  const getPriorAuthStatusStyles = (action) => {
    const normalized = String(action || "").toLowerCase();
    if (normalized === "approved") {
      return {
        backgroundColor: "#EAF5E8",
        color: "#6C8F2A",
      };
    }
    if (normalized === "pending") {
      return {
        backgroundColor: "#FFF6DF",
        color: "#9A6C00",
      };
    }
    if (normalized === "denied" || normalized === "declined") {
      return {
        backgroundColor: "#F8E9EB",
        color: "#CC0000",
      };
    }
    return {
      backgroundColor: "#ECECF1",
      color: "#403C6F",
    };
  };

  return (
    <div
      className="member-details-page"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100% - 51px)",
        backgroundColor: "#F7F8FB",
        overflowX: "clip",
      }}
      ref={pageRootRef}
    >
      <ContentContainer
        sx={{
          flex: 1,
          transform: `scale(${containerScale})`,
          transformOrigin: "top left",
          width: containerScale < 1 ? "111%" : "100%",
          position: "relative",
          zIndex: 1,
        }}
        component="section"
        tabIndex={0}
        onKeyDown={(e) => {
          const el = e.currentTarget;
          const step = 40;
          switch (e.key) {
            case "ArrowDown":
              el.scrollBy({ top: step, behavior: "smooth" });
              e.preventDefault();
              break;
            case "ArrowUp":
              el.scrollBy({ top: -step, behavior: "smooth" });
              e.preventDefault();
              break;
            case "PageDown":
              el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
              e.preventDefault();
              break;
            case "PageUp":
              el.scrollBy({ top: -el.clientHeight, behavior: "smooth" });
              e.preventDefault();
              break;
            case "Home":
              el.scrollTo({ top: 0, behavior: "smooth" });
              e.preventDefault();
              break;
            case "End":
              el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
              e.preventDefault();
              break;
            default:
              break;
          }
        }}
      >
        <Container
          ref={containerRef}
          maxWidth={false}
          disableGutters
          sx={{
            padding: "0 12px",
            flex: 1,
            display: { xs: "block", md: "flex" },
            flexDirection: "column",
            overflowY: { xs: "auto", md: "hidden" },
            overflowX: "hidden",
            height: {
              xs: "auto",
              md: computedHeight ? `${computedHeight}px` : "100%",
            },
            maxHeight: {
              xs: "none",
              md: computedHeight ? `${computedHeight}px` : "100%",
            },
            minHeight: 0,
            backgroundColor: "#F7F8FB",

            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
              {
                background: "#555",
              },
          }}
          component="section"
          tabIndex={0}
          onKeyDown={(e) => {
            const el = e.currentTarget;
            const step = 40;
            switch (e.key) {
              case "ArrowDown":
                el.scrollBy({ top: step, behavior: "smooth" });
                e.preventDefault();
                break;
              case "ArrowUp":
                el.scrollBy({ top: -step, behavior: "smooth" });
                e.preventDefault();
                break;
              case "PageDown":
                el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
                e.preventDefault();
                break;
              case "PageUp":
                el.scrollBy({ top: -el.clientHeight, behavior: "smooth" });
                e.preventDefault();
                break;
              case "Home":
                el.scrollTo({ top: 0, behavior: "smooth" });
                e.preventDefault();
                break;
              case "End":
                el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                e.preventDefault();
                break;
              default:
                break;
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              width: "100%",
              gap: 2,
              flexShrink: 0,
            }}
          >
            <Box
              sx={getBreadcrumbBoxSx(
                !isClinicalNoteView && memberData?.pcaDirectiveEnabled,
              )}
            >
              <Breadcrumb items={getBreadcrumbItems()} />
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 auto", md: "1 1 0%" },
                minWidth: 0,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <PCADirectiveDisplay
                pcaDirective={memberData.pcaDirective}
                pcaDirectiveEnabled={memberData.pcaDirectiveEnabled}
              />
            </Box>
          </Box>

          {!isClinicalNoteView && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                alignItems: "stretch",
                mt: 2,
                flex: { xs: "1 1 auto", md: "1 1 0%" },
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  height: { xs: "auto", md: "100%" },
                  maxHeight: { xs: "none", md: "100%" },
                  flex: { xs: "1 1 auto", md: "0 0 32%" },
                  maxWidth: { md: "400px" },
                  minWidth: { md: "280px" },
                  "@media (min-width: 1800px)": {
                    flex: "0 0 36%",
                    maxWidth: "470px",
                    minWidth: "360px",
                  },
                  "@media (min-width: 1849px)": {
                    flex: "0 0 38%",
                    maxWidth: "500px",
                    minWidth: "380px",
                  },
                  overflowY: { xs: "visible", md: "auto" },
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#C4C2D3",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#A09EBA",
                  },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    padding: "12px 14px",
                    marginTop: 0,
                    marginBottom: 0,
                    border: "1px solid #E8E8E9",
                    position: { md: "sticky" },
                    top: { md: 8 },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  ></Box>
                  <Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap="12px"
                      flexWrap="wrap"
                      sx={{ mb: 2, mt: 1 }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          backgroundColor: "#ECE9FA",
                          color: "#5B50A4",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "18px",
                          flexShrink: 0,
                        }}
                      >
                        {memberInitials}
                      </Box>
                      <Box
                        sx={{ minWidth: 0, flex: 1 }}
                        ref={headerContainerRef}
                      >
                        <Box sx={getChipRowSx(wrapChipsBelow)}>
                          <Typography
                            ref={nameRef}
                            variant="h5"
                            fontWeight={700}
                            fontSize="18px"
                            sx={{
                              color: "#403C6F",
                              lineHeight: 1.2,
                              wordBreak: "break-word",
                            }}
                          >
                            {displayName}
                          </Typography>
                          <Box
                            ref={chipsRef}
                            sx={{
                              display: "inline-flex",
                              gap: 1,
                              alignItems: "center",
                              flexWrap: "nowrap",
                            }}
                          >
                            <Chip
                              label={memberData.status}
                              color="success"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                backgroundColor: "#e8f5e9",
                                color: "#2e7d32",
                                border: "1px solid #45B184",
                                height: "22px",
                                "& .MuiChip-label": {
                                  padding: "0 8px",
                                  fontSize: "0.75rem",
                                },
                              }}
                            />
                            <Chip
                              label={memberData?.dispositionStatus}
                              color="success"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                backgroundColor: "#ECECF1",
                                color: "#403C6F",
                                border: 0,
                                height: "22px",
                                "& .MuiChip-label": {
                                  padding: "0 8px",
                                  fontSize: "0.75rem",
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        {memberId?.includes("-") &&
                          totalOpenOpportunities === 0 && (
                            <Typography
                              sx={{
                                color: placeholderText,
                                fontSize: "12px",
                                fontStyle: "italic",
                                mt: 0.3,
                              }}
                            >
                              No open opportunities.
                            </Typography>
                          )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            mt: 0.8,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#7F7C9F",
                              fontSize: "12px",
                              fontWeight: 400,
                              lineHeight: 1.2,
                            }}
                          >
                            Assigned PCA:
                          </Typography>
                          <Typography
                            sx={{
                              color: "#403C6F",
                              fontSize: "14px",
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            {memberData?.assignedPca || "N/A"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            mt: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#7F7C9F",
                              fontSize: "12px",
                              fontWeight: 400,
                              lineHeight: 1.2,
                            }}
                          >
                            Assigned RN:
                          </Typography>
                          <Typography
                            sx={{
                              color: "#403C6F",
                              fontSize: "14px",
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            {memberData?.assignedRn || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {showFamilyMembersSection && (
                      <Box
                        sx={{
                          borderRadius: "12px",
                          border: "1px solid #E8E8E9",
                          overflow: "hidden",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.2,
                            backgroundColor: "#F7F8FB",
                            borderBottom: "1px solid #E8E8E9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#403C6F",
                              fontWeight: 700,
                              fontSize: "14px",
                            }}
                          >
                            Family Members
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => toggleLeftSection("familyMembers")}
                            aria-label="Toggle family members section"
                            sx={{
                              width: "22px",
                              height: "22px",
                              color: "#6E6D92",
                            }}
                          >
                            <KeyboardArrowDownIcon
                              sx={{
                                fontSize: "18px",
                                transform: getSectionArrowTransform(
                                  leftSectionsOpen.familyMembers,
                                ),
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </IconButton>
                        </Box>
                        {leftSectionsOpen.familyMembers &&
                          isFamilyMembersLoading && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 2,
                              }}
                            >
                              <Lottie
                                animationData={loaderAnimation}
                                loop
                                role="status"
                                aria-label="Loading family members"
                                style={{ width: 64, height: 64 }}
                              />
                            </Box>
                          )}
                        {leftSectionsOpen.familyMembers &&
                          !isFamilyMembersLoading &&
                          otherFamilyMembers.map((member, index) => {
                            const rawFullName =
                              [member.firstName, member.lastName]
                                .filter(Boolean)
                                .join(" ") || "N/A";
                            let fullName = rawFullName;
                            if (rawFullName !== "N/A" && !isShowPhiEnabled) {
                              fullName = getNameVariants(
                                rawFullName,
                                isShowPhiEnabled,
                              ).maskedMiddle;
                            }
                            // Only a family member with a program/opportunity
                            // (a real member id, and - when the subscriber
                            // search reports it - at least one HALO program)
                            // gets a profile link under their name.
                            const isEnrolled =
                              member.memberId != null &&
                              (!Array.isArray(member.hasHaloProgram) ||
                                member.hasHaloProgram.length > 0);
                            // The cardholder (relCode 1) is always reachable,
                            // even with no opportunity of their own - they're
                            // the family's anchor. With no member id yet, their
                            // profile opens through the same synthetic
                            // "{subscriberNum}-{personCode}" id the outreach
                            // list uses.
                            const isCardholder =
                              Number.parseInt(member.relCode, 10) === 1;
                            const canOpenProfile = isEnrolled || isCardholder;
                            const profileId =
                              member.memberId ??
                              `${member.subscriberNum}-${member.personCode}`;
                            const relCodeText =
                              member.relCode == null || member.relCode === ""
                                ? "0"
                                : String(member.relCode);
                            return (
                              <Box
                                key={`${member.subscriberNum}-${member.personCode}-${index}`}
                                sx={{
                                  px: 2,
                                  py: 0.95,
                                  borderBottom:
                                    index === otherFamilyMembers.length - 1
                                      ? "none"
                                      : "1px solid #F0F0F3",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) auto",
                                    alignItems: "baseline",
                                    columnGap: 2,
                                  }}
                                >
                                  {canOpenProfile ? (
                                    <Typography
                                      onClick={() =>
                                        navigate(`/members/${profileId}`)
                                      }
                                      sx={{
                                        color: linkColor,
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        "&:hover": { color: linkHoverColor },
                                      }}
                                    >
                                      {fullName}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      sx={{
                                        color: "#403C6F",
                                        fontSize: "14px",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {fullName}
                                    </Typography>
                                  )}
                                  <Typography
                                    sx={{
                                      color: "#7F7C9F",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      textAlign: "right",
                                    }}
                                  >
                                    {relCodeText} -{" "}
                                    {getRelCodeLabel(member.relCode)}
                                  </Typography>
                                </Box>
                                {!isEnrolled && (
                                  <Typography
                                    sx={{
                                      color: placeholderText,
                                      fontSize: "12px",
                                      fontStyle: "italic",
                                      mt: 0.3,
                                    }}
                                  >
                                    No open opportunities.
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    )}

                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid #E8E8E9",
                        overflow: "hidden",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.2,
                          backgroundColor: "#F7F8FB",
                          borderBottom: "1px solid #E8E8E9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "14px",
                          }}
                        >
                          Member Demographic
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleLeftSection("demographics")}
                          aria-label="Toggle demographics section"
                          sx={{
                            width: "22px",
                            height: "22px",
                            color: "#6E6D92",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: "18px",
                              transform: getSectionArrowTransform(
                                leftSectionsOpen.demographics,
                              ),
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </IconButton>
                      </Box>
                      {leftSectionsOpen.demographics &&
                        [
                          {
                            label: "Member ID",
                            value: getMemberIdDisplay(
                              memberData,
                              isShowPhiEnabled,
                            ),
                          },
                          {
                            label: "Person Code",
                            value: memberData.personCode || "N/A",
                          },
                          {
                            label: "Date of Birth",
                            value: getDobDisplayValue(memberData.dob),
                          },
                          {
                            label: "Age / Gender",
                            value: getAgeGenderDisplay(
                              memberData,
                              isShowPhiEnabled,
                            ),
                          },
                          {
                            label: "Timezone",
                            value: memberData.timeZone || "N/A",
                          },
                          {
                            label: "Carrier",
                            value: getNameWithCodeDisplay(
                              memberData.carrierName,
                              memberData.carrierCode,
                            ),
                          },
                          {
                            label: "Group",
                            value: getNameWithCodeDisplay(
                              memberData.groupName,
                              memberData.groupNum,
                            ),
                          },
                          {
                            label: "Group End Date",
                            value: formatDate(memberData.groupEndDate),
                          },
                          {
                            label: "Care Guide Effective Date",
                            value: formatDate(
                              memberData.careGuideEffectiveDate,
                            ),
                          },
                        ].map((row, index, arr) => (
                          <Box
                            key={row.label}
                            sx={{
                              px: 2,
                              py: 0.95,
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 2,
                              borderBottom:
                                index === arr.length - 1
                                  ? "none"
                                  : "1px solid #F0F0F3",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#7F7C9F",
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.label}
                            </Typography>
                            <Tooltip
                              title={
                                row.value && row.value !== "N/A"
                                  ? row.value
                                  : ""
                              }
                              arrow
                              placement="top"
                              disableHoverListener={
                                !row.value || row.value === "N/A"
                              }
                            >
                              <Typography
                                sx={{
                                  color: "#403C6F",
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  maxWidth: "56%",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.value}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ))}
                    </Box>

                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid #E8E8E9",
                        overflow: "hidden",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.2,
                          backgroundColor: "#F7F8FB",
                          borderBottom: "1px solid #E8E8E9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#403C6F",
                              fontWeight: 700,
                              fontSize: "14px",
                            }}
                          >
                            Contact Information
                          </Typography>
                          <Tooltip title="Edit contact information" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (!canWriteModule) return;
                                setContactModalOpen(true);
                              }}
                              disabled={!canWriteModule}
                              aria-disabled={!canWriteModule}
                              aria-label="Edit contact information"
                              sx={{
                                width: "22px",
                                height: "22px",
                                borderRadius: "6px",
                                border: "1px solid #E8E8E9",
                                "&:hover": {
                                  backgroundColor: "rgba(232, 232, 233, 0.2)",
                                },
                                "&.Mui-disabled": {
                                  cursor: "not-allowed",
                                  color: "#999893",
                                  pointerEvents: "auto !important",
                                },
                              }}
                            >
                              <EditOutlinedIcon
                                sx={{
                                  color: canWriteModule ? "#6E6D92" : "#999893",
                                  fontSize: "16px",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => toggleLeftSection("contact")}
                          aria-label="Toggle contact section"
                          sx={{
                            width: "22px",
                            height: "22px",
                            color: "#6E6D92",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: "18px",
                              transform: getSectionArrowTransform(
                                leftSectionsOpen.contact,
                              ),
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </IconButton>
                      </Box>
                      {leftSectionsOpen.contact &&
                        [
                          {
                            label: "Preferred Contact",
                            value: (
                              <ContactInfo
                                memberData={memberData}
                                isShowPhiEnabled={isShowPhiEnabled}
                              />
                            ),
                          },
                          {
                            label: "Address",
                            value: maskedAddressText,
                            tooltip: maskedAddressText,
                          },
                          {
                            label: "Known Allergies",
                            value: (
                              <Tooltip
                                title={
                                  knownAllergiesDisplay.shouldTruncate
                                    ? knownAllergiesDisplay.rawAllergies
                                    : ""
                                }
                                arrow
                                placement="top"
                                disableHoverListener={
                                  !knownAllergiesDisplay.shouldTruncate
                                }
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 0.8,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "#403C6F",
                                      fontSize: "14px",
                                      fontWeight: 700,
                                      maxWidth: "100%",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      minWidth: "30px",
                                    }}
                                  >
                                    {knownAllergiesDisplay.text}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ),
                          },
                          {
                            label: "Language",
                            value: getCapitalizedOrNA(memberData.language),
                          },
                          {
                            label: "Text Consent",
                            value: getTextConsentDisplay(
                              memberData.textingConsentObtained,
                            ),
                          },
                        ].map((row, index, arr) => {
                          const isTextConsentRow = row.label === "Text Consent";
                          const isReactNodeValue =
                            row.value !== null && typeof row.value === "object";
                          let valueColor = "#403C6F";
                          if (isTextConsentRow) {
                            if (memberData.textingConsentObtained) {
                              valueColor = "#45B184";
                            } else {
                              valueColor = "#CC0000";
                            }
                          }

                          return (
                            <Box
                              key={row.label}
                              sx={{
                                px: 2,
                                py: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 2,
                                borderBottom:
                                  index === arr.length - 1
                                    ? "none"
                                    : "1px solid #F0F0F3",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#7F7C9F",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.label}
                              </Typography>
                              {isReactNodeValue ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    maxWidth: "56%",
                                    minWidth: 0,
                                  }}
                                >
                                  {row.value}
                                </Box>
                              ) : (
                                <Tooltip
                                  title={
                                    row.tooltip && row.tooltip !== "N/A"
                                      ? row.tooltip
                                      : ""
                                  }
                                  arrow
                                  placement="top"
                                  disableHoverListener={
                                    !row.tooltip || row.tooltip === "N/A"
                                  }
                                >
                                  <Typography
                                    sx={{
                                      color: valueColor,
                                      fontSize: "14px",
                                      fontWeight: 700,
                                      maxWidth: "56%",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {row.value}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                    {(copayOpportunity ||
                      hasCopayData ||
                      memberData.copayExecutionStatus ||
                      (copayOpportunities &&
                        copayOpportunities.length > 0)) && (
                      <Box sx={{ width: "100%", mb: 2 }}>
                        {/* Scenario Switcher Tabs */}

                        <Box
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid #E8E8E9",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 1.2,
                              backgroundColor: "#F7F8FB",
                              borderBottom: "1px solid #E8E8E9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#403C6F",
                                fontWeight: 700,
                                fontSize: "14px",
                              }}
                            >
                              Copay Card
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => toggleLeftSection("copayCard")}
                              aria-label="Toggle copay details section"
                              sx={{
                                width: "22px",
                                height: "22px",
                                color: "#6E6D92",
                              }}
                            >
                              <KeyboardArrowDownIcon
                                sx={{
                                  fontSize: "18px",
                                  transform: getSectionArrowTransform(
                                    leftSectionsOpen.copayCard,
                                  ),
                                  transition: "transform 0.2s ease",
                                }}
                              />
                            </IconButton>
                          </Box>

                          {leftSectionsOpen.copayCard && (
                            <Box sx={{ p: 2 }}>
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  tableLayout: "fixed",
                                }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      borderBottom: "1px solid #E8E8E9",
                                    }}
                                  >
                                    <th
                                      style={{
                                        textAlign: "left",
                                        paddingBottom: "8px",
                                        color: "#7F7C9F",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        width: "40%",
                                      }}
                                    >
                                      Drug
                                    </th>
                                    <th
                                      style={{
                                        textAlign: "left",
                                        paddingBottom: "8px",
                                        color: "#7F7C9F",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        width: "35%",
                                      }}
                                    >
                                      Status
                                    </th>
                                    <th
                                      style={{
                                        textAlign: "right",
                                        paddingBottom: "8px",
                                        color: "#7F7C9F",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        width: "25%",
                                      }}
                                    >
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(copayOpportunities.length > 0
                                    ? copayOpportunities.filter(
                                        (opp) =>
                                          opp.drug &&
                                          opp.drug !== "Unknown" &&
                                          opp.drug.toLowerCase() !== "unknown",
                                      )
                                    : [
                                        {
                                          drug: "Veozah 45 MG",
                                          ndc: "00469266030",
                                          programId: "mock-opp-id",
                                          opportunityId: "mock-opp-id",
                                        },
                                      ]
                                  ).map((opp) => {
                                    const oppId =
                                      opp.programId ||
                                      opp.memberProgramId ||
                                      opp.opportunityId ||
                                      opp.id;
                                    const details =
                                      copayDetailsMap[oppId] || {};

                                    // Determine status and color
                                    let status = "N/A";
                                    let statusColor = "#6E6D92";

                                    const savedStatus =
                                      details.copayExecutionStatus || "";

                                    if (
                                      savedStatus === "COMPLETE" ||
                                      savedStatus === "COMPLETED" ||
                                      savedStatus === "SUCCESS" ||
                                      savedStatus === "MANUAL"
                                    ) {
                                      status = "SUCCESS";
                                    } else if (savedStatus === "PENDING") {
                                      status = "PENDING";
                                    } else if (savedStatus === "FAILED") {
                                      status = "FAILED";
                                    } else if (
                                      savedStatus !== "NOT_STARTED" &&
                                      copayScenario === "scenario2"
                                    ) {
                                      status = "PENDING";
                                    }

                                    if (status === "SUCCESS") {
                                      statusColor = "#2D9D42";
                                    } else if (status === "PENDING") {
                                      statusColor = "#2B6CB0";
                                    } else if (status === "FAILED") {
                                      statusColor = "#E53E3E";
                                    }

                                    const isCompleted = status === "SUCCESS";

                                    return (
                                      <tr
                                        key={oppId}
                                        style={{
                                          borderBottom: "1px solid #F0F0F3",
                                        }}
                                      >
                                        <td
                                          style={{
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              color: "#403C6F",
                                              fontSize: "13px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {opp.drug || "N/A"}
                                          </Typography>
                                          {opp.ndc && (
                                            <Typography
                                              sx={{
                                                color: "#7F7C9F",
                                                fontSize: "11px",
                                                fontWeight: 400,
                                              }}
                                            >
                                              NDC: {opp.ndc}
                                            </Typography>
                                          )}
                                        </td>
                                        <td
                                          style={{
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              color: statusColor,
                                              fontSize: "13px",
                                              fontWeight: 700,
                                            }}
                                          >
                                            {status}
                                          </Typography>
                                        </td>
                                        <td
                                          style={{
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            textAlign: "right",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          {isCompleted ? (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                gap: 0.5,
                                              }}
                                            >
                                              <Tooltip title="View Card Details">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    setSelectedCopayOpportunity(
                                                      opp,
                                                    );
                                                    setIsCopayCardModalViewOnly(
                                                      true,
                                                    );
                                                    setCopayCardModalInitialData(
                                                      {
                                                        bin:
                                                          details.copayBin ||
                                                          memberData.copayBin,
                                                        pcn:
                                                          details.copayPcn ||
                                                          memberData.copayPcn,
                                                        grpId:
                                                          details.copayGrpId ||
                                                          memberData.copayGrpId,
                                                        memberId:
                                                          details.copayMemberId ||
                                                          memberData.copayMemberId,
                                                        expirationDate:
                                                          details.copayExpirationDate ||
                                                          memberData.copayExpirationDate,
                                                        executionStatus:
                                                          details.copayExecutionStatus ||
                                                          memberData.copayExecutionStatus,
                                                        annualValue:
                                                          details.copayAnnualValue ||
                                                          memberData.copayAnnualValue ||
                                                          "",
                                                        annualBenefitAmount:
                                                          details.annualBenefitAmount ||
                                                          Number.parseFloat(
                                                            String(
                                                              memberData.copayAnnualValue ||
                                                                "",
                                                            ).replace(
                                                              /[^0-9.]/g,
                                                              "",
                                                            ),
                                                          ) ||
                                                          0,
                                                        annualBenefitExpirationDate:
                                                          details.annualBenefitExpirationDate ||
                                                          memberData.annualBenefitExpirationDate ||
                                                          "",
                                                        perFillAmount:
                                                          resolvePerFillAmount(
                                                            details,
                                                            opp,
                                                          ),
                                                        registrationRequestStatus:
                                                          details.copayRegistrationRequestStatus ||
                                                          memberData.copayRegistrationRequestStatus ||
                                                          "",
                                                        annualBenefitNotes:
                                                          details.annualBenefitNotes ||
                                                          details.annualBenefitNote ||
                                                          memberData.annualBenefitNotes ||
                                                          memberData.annualBenefitNote ||
                                                          opp?.annualBenefitNotes ||
                                                          opp?.annualBenefitNote ||
                                                          "",
                                                        regUrl:
                                                          details.regUrl ||
                                                          details.copayRegUrl ||
                                                          opp?.regUrl ||
                                                          opp?.copayRegUrl ||
                                                          opp?.program
                                                            ?.regUrl ||
                                                          opp?.websiteUrl ||
                                                          opp?.url ||
                                                          "",
                                                      },
                                                    );
                                                    setIsCopayCardModalOpen(
                                                      true,
                                                    );
                                                  }}
                                                  sx={{
                                                    p: 0.25,
                                                    color: "#6E6D92",
                                                  }}
                                                >
                                                  <VisibilityOutlinedIcon
                                                    sx={{ fontSize: "18px" }}
                                                  />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Submit Again">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    setSelectedCopayOpportunity(
                                                      opp,
                                                    );
                                                    setIsCopayCardModalViewOnly(
                                                      false,
                                                    );
                                                    setCopayCardModalInitialData(
                                                      {
                                                        bin:
                                                          details.copayBin ||
                                                          memberData.copayBin,
                                                        pcn:
                                                          details.copayPcn ||
                                                          memberData.copayPcn,
                                                        grpId:
                                                          details.copayGrpId ||
                                                          memberData.copayGrpId,
                                                        memberId:
                                                          details.copayMemberId ||
                                                          memberData.copayMemberId,
                                                        expirationDate:
                                                          details.copayExpirationDate ||
                                                          memberData.copayExpirationDate,
                                                        executionStatus:
                                                          details.copayExecutionStatus ||
                                                          memberData.copayExecutionStatus,
                                                        annualValue:
                                                          details.copayAnnualValue ||
                                                          memberData.copayAnnualValue ||
                                                          "",
                                                        annualBenefitAmount:
                                                          details.annualBenefitAmount ||
                                                          Number.parseFloat(
                                                            String(
                                                              memberData.copayAnnualValue ||
                                                                "",
                                                            ).replace(
                                                              /[^0-9.]/g,
                                                              "",
                                                            ),
                                                          ) ||
                                                          0,
                                                        annualBenefitExpirationDate:
                                                          details.annualBenefitExpirationDate ||
                                                          memberData.annualBenefitExpirationDate ||
                                                          "",
                                                        perFillAmount:
                                                          resolvePerFillAmount(
                                                            details,
                                                            opp,
                                                          ),
                                                        registrationRequestStatus:
                                                          details.copayRegistrationRequestStatus ||
                                                          memberData.copayRegistrationRequestStatus ||
                                                          "",
                                                        annualBenefitNotes:
                                                          details.annualBenefitNotes ||
                                                          details.annualBenefitNote ||
                                                          memberData.annualBenefitNotes ||
                                                          memberData.annualBenefitNote ||
                                                          opp?.annualBenefitNotes ||
                                                          opp?.annualBenefitNote ||
                                                          "",
                                                        regUrl:
                                                          details.regUrl ||
                                                          details.copayRegUrl ||
                                                          opp?.regUrl ||
                                                          opp?.copayRegUrl ||
                                                          opp?.program
                                                            ?.regUrl ||
                                                          opp?.websiteUrl ||
                                                          opp?.url ||
                                                          "",
                                                      },
                                                    );
                                                    setIsCopayCardModalOpen(
                                                      true,
                                                    );
                                                  }}
                                                  sx={{
                                                    p: 0.25,
                                                    color: "#6E6D92",
                                                  }}
                                                >
                                                  <RefreshIcon
                                                    sx={{ fontSize: "18px" }}
                                                  />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          ) : (
                                            <Button
                                              size="small"
                                              onClick={() => {
                                                setSelectedCopayOpportunity(
                                                  opp,
                                                );
                                                setIsCopayCardModalViewOnly(
                                                  false,
                                                );
                                                setCopayCardModalInitialData(
                                                  null,
                                                );
                                                setIsCopayCardModalOpen(true);
                                              }}
                                              sx={{
                                                textTransform: "none",
                                                fontWeight: 600,
                                                fontSize: "11px",
                                                color: "#403C6F",
                                                border: "1px solid #E8E8E9",
                                                borderRadius: "4px",
                                                px: 1,
                                                py: 0.2,
                                                height: "20px",
                                                minWidth: 0,
                                                "&:hover": {
                                                  backgroundColor:
                                                    "rgba(232, 232, 233, 0.2)",
                                                },
                                              }}
                                            >
                                              Get Card
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid #E8E8E9",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.2,
                          backgroundColor: "#F7F8FB",
                          borderBottom: "1px solid #E8E8E9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "14px",
                          }}
                        >
                          Accumulators
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleLeftSection("accumulators")}
                          aria-label="Toggle accumulators section"
                          sx={{
                            width: "22px",
                            height: "22px",
                            color: "#6E6D92",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: "18px",
                              transform: getSectionArrowTransform(
                                leftSectionsOpen.accumulators,
                              ),
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </IconButton>
                      </Box>
                      {leftSectionsOpen.accumulators && (
                        <Box sx={{ px: 2, py: 1.25 }}>
                          <Box
                            sx={{
                              px: 0,
                              py: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "left",
                                mb: 0.5,
                                borderBottom: "1px solid #E8E8E9",
                                pb: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#7F7C9F",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                <Tooltip
                                  title="Beneficiary Date"
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "inherit",
                                      fontWeight: "inherit",
                                      color: "inherit",
                                    }}
                                  >
                                    Beneficiary Date
                                  </Typography>
                                </Tooltip>
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#403C6F",
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                {hasFetchedOop ? (
                                  <Tooltip
                                    title={formatDate(
                                      memberData.benefitStartDate,
                                    )}
                                    arrow
                                    placement="top"
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: "inherit",
                                        fontWeight: "inherit",
                                        color: "inherit",
                                      }}
                                    >
                                      {formatDate(memberData.benefitStartDate)}
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <LoadingWaveDots />
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          {accumulatorRows.map((row) => (
                            <Box
                              key={row.label}
                              sx={{ mb: 1.7, "&:last-child": { mb: 0.2 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: "#7F7C9F",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                  }}
                                >
                                  <Tooltip
                                    title={row.label}
                                    arrow
                                    placement="top"
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: "inherit",
                                        fontWeight: "inherit",
                                        color: "inherit",
                                      }}
                                    >
                                      {row.label}
                                    </Typography>
                                  </Tooltip>
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "#403C6F",
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {hasFetchedOop ? (
                                    <Tooltip
                                      title={row.valueText}
                                      arrow
                                      placement="top"
                                      disableHoverListener={
                                        row.valueText === "N/A"
                                      }
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: "inherit",
                                          fontWeight: "inherit",
                                          color: "inherit",
                                        }}
                                      >
                                        {row.valueText}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <LoadingWaveDots />
                                  )}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  height: "8px",
                                  width: "100%",
                                  borderRadius: "999px",
                                  backgroundColor: "#E7E7EE",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${hasFetchedOop ? row.pct : 0}%`,
                                    borderRadius: "999px",
                                    backgroundColor: row.color,
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid #E8E8E9",
                        overflow: "hidden",
                        mt: 2,
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.2,
                          backgroundColor: "#F7F8FB",
                          borderBottom: "1px solid #E8E8E9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "14px",
                          }}
                        >
                          Prior-Auth Information
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleLeftSection("priorAuth")}
                          aria-label="Toggle prior auth section"
                          sx={{
                            width: "22px",
                            height: "22px",
                            color: "#6E6D92",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: "18px",
                              transform: getSectionArrowTransform(
                                leftSectionsOpen.priorAuth,
                              ),
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </IconButton>
                      </Box>

                      {leftSectionsOpen.priorAuth &&
                        (() => {
                          if (isPriorAuthLoading) {
                            return (
                              <Box sx={{ overflowX: "auto" }}>
                                <Box sx={{ minWidth: 700, p: 2 }}>
                                  <Box
                                    sx={{
                                      border: "1px solid #EAEAEA",
                                      borderRadius: "10px",
                                      overflow: "hidden",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "28% 21% 21% 10% 20%",
                                        px: 2,
                                        py: 1,
                                        backgroundColor: "#FFFFFF",
                                        borderBottom: "1px solid #ECECEC",
                                        color: "#9A9A9A",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        letterSpacing: "0.03em",
                                      }}
                                    >
                                      <Box>Drug</Box>
                                      <Box>Eff. Date</Box>
                                      <Box>Term Date</Box>
                                      <Box>Action</Box>
                                      <Box>Last Changed On</Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        p: 2,
                                        textAlign: "center",
                                        color: "#6E6D92",
                                      }}
                                    >
                                      Loading Prior Authorizations...
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          }
                          if (!priorAuthData || priorAuthData.length === 0) {
                            return (
                              <Box
                                sx={{
                                  p: 2.5,
                                  textAlign: "center",
                                  color: "#6E6D92",
                                  fontSize: "14px",
                                }}
                              >
                                No Prior Authorizations found.
                              </Box>
                            );
                          }
                          return (
                            <Box sx={{ overflowX: "auto" }}>
                              <Box sx={{ minWidth: 700, p: 2 }}>
                                <Box
                                  sx={{
                                    border: "1px solid #EAEAEA",
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns:
                                        "28% 21% 21% 10% 20%",
                                      px: 2,
                                      py: 1,
                                      backgroundColor: "#FFFFFF",
                                      borderBottom: "1px solid #ECECEC",
                                      color: "#9A9A9A",
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      letterSpacing: "0.03em",
                                    }}
                                  >
                                    <Box>Drug</Box>
                                    <Box>Eff. Date</Box>
                                    <Box>Term Date</Box>
                                    <Box>Action</Box>
                                    <Box>Last Changed On</Box>
                                  </Box>
                                  {priorAuthData.map((row, index) => {
                                    const statusStyles =
                                      getPriorAuthStatusStyles(row.status);
                                    const actionLetter =
                                      getPriorAuthActionLetter(row.status);
                                    return (
                                      <Box
                                        key={`${row.drugName}-${index}`}
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "28% 21% 21% 10% 20%",
                                          px: 2,
                                          py: 1.15,
                                          borderBottom:
                                            index === priorAuthData.length - 1
                                              ? "none"
                                              : "1px solid #ECECEC",
                                          alignItems: "center",
                                          backgroundColor:
                                            index % 2 === 0
                                              ? "#FFFFFF"
                                              : "#FCFCFD",
                                        }}
                                      >
                                        <Box sx={{ minWidth: 0, pr: 1 }}>
                                          <Typography
                                            sx={{
                                              color: "#1F2937",
                                              fontSize: "14px",
                                              fontWeight: 500,
                                              lineHeight: 1.2,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            <Tooltip
                                              title={row.medication || "N/A"}
                                              arrow
                                              placement="top"
                                              disableHoverListener={
                                                !row.medication
                                              }
                                            >
                                              <Typography
                                                component="span"
                                                sx={{
                                                  fontSize: "inherit",
                                                  fontWeight: "inherit",
                                                  color: "inherit",
                                                }}
                                              >
                                                {row.medication || "N/A"}
                                              </Typography>
                                            </Tooltip>
                                          </Typography>
                                          <Typography
                                            sx={{
                                              color: "#8A8A8A",
                                              fontSize: "11px",
                                              mt: 0.3,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            GPI: {row.gpi || "N/A"}
                                          </Typography>
                                        </Box>

                                        <Typography
                                          sx={{
                                            color: "#1F2937",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {formatDate(row.effDate)}
                                        </Typography>

                                        <Typography
                                          sx={{
                                            color: "#1F2937",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {formatDate(row.termDate)}
                                        </Typography>

                                        <Box>
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              width: 28,
                                              height: 24,
                                              borderRadius: "6px",
                                              backgroundColor:
                                                statusStyles.backgroundColor,
                                              color: statusStyles.color,
                                              fontSize: "12px",
                                              fontWeight: 700,
                                              lineHeight: 1,
                                            }}
                                          >
                                            {actionLetter}
                                          </Box>
                                        </Box>

                                        <Typography
                                          sx={{
                                            color: "#1F2937",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            pl: 1,
                                          }}
                                        >
                                          {formatDate(row.createdAt)}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })()}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  height: { xs: "auto", md: "100%" },
                  maxHeight: { xs: "none", md: "100%" },
                  display: "flex",
                  flexDirection: "column",
                  flex: { xs: "1 1 auto", md: "1 1 0%" },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={0}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mr: 1,
                      alignItems: "center",
                    }}
                  ></Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    height: { xs: "auto", md: "100%" },
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      "& > div": {
                        width: "100%",
                        maxWidth: "100%",
                        boxShadow: "none",
                        borderRadius: 0,
                        padding: 0,
                        height: "100%",
                      },
                    }}
                  >
                    <OpportunitiesTable
                      key={opportunitiesReloadKey}
                      showMonitoringReason={
                        fromMemberMonitoring || location?.state?.memberBadge
                      }
                      setDispositionOpportunity={setDispositionOpportunity}
                      setProgramOptions={setProgramOptions}
                      setProgramDrugMap={setProgramDrugMap}
                      memberId={memberId}
                      title={dispositionTitle ?? "Add Disposition"}
                      setMonitoringReasons={setMonitoringReasons}
                      fromMemberMonitoring={fromMemberMonitoring}
                      canWrite={canWriteModule}
                      canEdit={canWriteModule}
                      reloadKey={opportunitiesReloadKey}
                      onOpenDisposition={(item) => {
                        if (item) {
                          setDispositionOpportunity((prev) => ({
                            ...prev,
                            selected: [item],
                            methodDisplay:
                              item.recentActivity?.outreachMethodDisplayName ||
                              "",
                          }));
                        }
                        setIsDispositionModalOpen(true);
                        setDispositionTitle("Add Disposition");
                      }}
                      setProgramMap={setProgramMap}
                      onLoadingChange={setIsOpportunitiesLoading}
                      isShowPhiEnabled={isShowPhiEnabled}
                      canDelete={canDeleteModule}
                      onMedicationLoadingChange={setIsMedicationHistoryLoading}
                      onOutreachLoadingChange={setIsOutreachHistoryLoading}
                      notes={notes}
                      selectedNoteType={selectedNoteType}
                      searchQuery={searchQuery}
                      onSearchNotes={(query) => {
                        setSearchQuery(query || "");
                      }}
                      onNoteTypeChange={(types) => {
                        setSelectedNoteType(
                          Array.isArray(types) ? types : [types],
                        );
                      }}
                      onToggleNotePin={toggleNotePin}
                      onEditNote={handleEditNote}
                      setIsNoteModalOpen={setIsNoteModalOpen}
                      setIsEscalationModalOpen={() => {
                        setEditingEscalation(null);
                        setIsEscalationModalOpen(true);
                      }}
                      escalations={escalations}
                      onSelectEscalation={handleSelectEscalation}
                      activateEscalationsTabKey={activateEscalationsTabKey}
                      showHeaderActions={true}
                      externalOutcomeTriggerKey={externalOutcomeTriggerKey}
                      memberData={memberData}
                      onTabChange={(tabId) => {
                        setIsNotesTabActive(tabId === "notes");
                        if (tabId === "notes") {
                          setIsNotesLoading(true);
                        } else {
                          setIsNotesLoading(false);
                        }
                      }}
                      onGetCopayCard={(item) => {
                        setSelectedCopayOpportunity(item);
                        const oppId =
                          item?.programId ||
                          item?.memberProgramId ||
                          item?.opportunityId ||
                          item?.id;
                        const details =
                          (oppId && copayDetailsMap?.[oppId]) || null;
                        setCopayCardModalInitialData(details);
                        setIsCopayCardModalOpen(true);
                      }}
                      onCopayOpportunityFound={setCopayOpportunity}
                      onCopayOpportunitiesFound={setCopayOpportunities}
                      onTotalOpportunitiesChange={setTotalOpenOpportunities}
                      onOpenClinicalHistory={() =>
                        setIsClinicalHistoryOpen(true)
                      }
                      onReopenClinicalHistory={handleReopenClinicalNote}
                      onStartClinicalNote={handleStartClinicalNoteClick}
                      onOpenBiosimilarsOutcome={() => {
                        setSelectedBiosimilarsNote(null);
                        setIsBiosimilarsReadOnly(false);
                        setIsBiosimilarsOutcomeOpen(true);
                      }}
                      clinicalNotePrograms={clinicalNotePrograms}
                      programMap={programMap}
                      referenceData={assessmentReferenceData}
                      onScheduleSuccess={fetchMemberNotes}
                      onMemberDetailsRefresh={fetchMemberData}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {isClinicalNoteView && (
            <ClinicalNotePage
              ref={clinicalNoteRef}
              memberData={memberData}
              isShowPhiEnabled={isShowPhiEnabled}
              selectedProgram={selectedClinicalNoteProgram}
              assessmentType={selectedClinicalNoteType}
              clinicalAssessmentId={selectedClinicalAssessmentId || null}
              initialAssessmentData={initialAssessmentData}
              batchId={selectedClinicalNoteBatchId}
              readOnly={isClinicalNoteReadOnly}
              initialStartTime={selectedClinicalNoteStartTime}
              initialClinicalNotes={selectedClinicalNotes}
              programMap={programMap}
              clinicalNotePrograms={clinicalNotePrograms}
              memberClinicalPrograms={memberClinicalPrograms}
              isDraftSession={isDraftSession}
              onBack={() => {
                setSelectedClinicalAssessmentId(null);
                setSelectedClinicalNoteBatchId(null);
                setSelectedClinicalNoteStartTime(null);
                setSelectedClinicalNotes("");
                setInitialAssessmentData(null);
                setIsDraftSession(false);
                setIsClinicalNoteView(false);
              }}
              onSave={async (content) => {
                setSelectedClinicalAssessmentId(null);
                setSelectedClinicalNoteBatchId(null);
                setSelectedClinicalNoteStartTime(null);
                setSelectedClinicalNotes("");
                setInitialAssessmentData(null);
                setIsDraftSession(false);
                setIsClinicalNoteView(false);
                setOpportunitiesReloadKey((prev) => prev + 1);
              }}
            />
          )}

          {/* Assessment Discard Warning Dialog for Breadcrumbs */}
          <Dialog
            open={showAssessmentDiscardWarning}
            onClose={() => {
              setShowAssessmentDiscardWarning(false);
              pendingBreadcrumbActionRef.current = null;
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "16px",
                  p: 1.5,
                  maxWidth: "440px",
                  width: "100%",
                },
              },
            }}
          >
            <DialogTitle
              sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "#FEF0C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <WarningAmberIcon sx={{ color: "#DC6803", fontSize: 24 }} />
              </Box>
              <Typography
                component="span"
                sx={{ fontWeight: 600, color: "#101828", fontSize: "18px" }}
              >
                Save Assessment Changes?
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pb: 2, pt: 0.5 }}>
              <Typography
                sx={{ color: "#475467", fontSize: "14px", lineHeight: 1.5 }}
              >
                You have unsaved changes in this assessment. Please save your
                changes as a draft before leaving.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
              <Button
                onClick={() => {
                  setShowAssessmentDiscardWarning(false);
                  pendingBreadcrumbActionRef.current = null;
                }}
                variant="outlined"
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  borderColor: "#D0D5DD",
                  color: "#344054",
                  px: 2.5,
                  py: 0.8,
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                    borderColor: "#D0D5DD",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    if (
                      clinicalNoteRef.current?.saveDraft &&
                      !isClinicalNoteReadOnly
                    ) {
                      await clinicalNoteRef.current.saveDraft({ silent: true });
                    }
                  } catch (e) {
                    console.error(
                      "Failed to save draft on breadcrumb exit:",
                      e,
                    );
                  } finally {
                    setShowAssessmentDiscardWarning(false);
                    if (pendingBreadcrumbActionRef.current) {
                      const action = pendingBreadcrumbActionRef.current;
                      pendingBreadcrumbActionRef.current = null;
                      action();
                    } else {
                      setSelectedClinicalAssessmentId(null);
                      setSelectedClinicalNoteBatchId(null);
                      setSelectedClinicalNoteStartTime(null);
                      setSelectedClinicalNotes("");
                      setInitialAssessmentData(null);
                      setIsDraftSession(false);
                      setIsClinicalNoteView(false);
                    }
                  }
                }}
                variant="contained"
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  backgroundColor: "#403C6F",
                  color: "#FFFFFF",
                  px: 2.5,
                  py: 0.8,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#312e5e",
                    boxShadow: "none",
                  },
                }}
              >
                Save & Exit
              </Button>
            </DialogActions>
          </Dialog>

          <AddNoteModal
            open={isNoteModalOpen}
            onClose={() => {
              setIsNoteModalOpen(false);
              setEditingNote(null);
            }}
            programOptions={programMap}
            onSave={handleAddNote}
            hasPinnedNote={notes.some((note) => note.pinNote)}
            canDelete={canDeleteModule}
            initialData={
              editingNote
                ? {
                    noteTypes: [editingNote.type],
                    noteDetails: editingNote.content,
                    note: editingNote.note,
                    pinNote: editingNote.pinNote || false,
                    editedAt: editingNote.editedAt || null,
                    rating: editingNote.rating || null,
                    noteEntry: editingNote.noteEntry || null,
                    programName: editingNote.programName || null,
                    isArchived: editingNote.status === "ARCHIVED",
                  }
                : null
            }
            canWrite={canWriteModule}
          />
          <EscalationVersionHistoryModal
            open={isEscalationHistoryOpen}
            onClose={() => setIsEscalationHistoryOpen(false)}
            versions={escalationVersions}
            onSelectVersion={handleSelectEscalationVersion}
          />
          <CreateEscalationModal
            open={isEscalationModalOpen}
            onClose={closeEscalationModal}
            onSave={handleSaveEscalation}
            initialData={editingEscalation}
            readOnly={
              Boolean(editingEscalation) &&
              (!editingEscalation.isCurrent ||
                TERMINAL_ESCALATION_STATUSES.includes(editingEscalation.status))
            }
            memberId={getEscalationEntityId()}
          />
          <AddEditOutcomeModal
            open={isDispositionModalOpen}
            onClose={() => {
              setIsDispositionModalOpen(false);
              setExternalOutcomeTriggerKey(0);
            }}
            onSave={handleAddDisposition}
            title={dispositionTitle || "Add Disposition"}
            dispositionOpportunity={dispositionOpportunity}
            programOptions={programOptions}
            programDrugMap={programDrugMap}
            monitoringReason={monitoringReasons}
            memberId={memberId}
            memberBadgeValue={location?.state?.memberBadge}
            showMonitoringReason={fromMemberMonitoring}
            canWrite={canWriteModule}
            outreachData={outreachData}
            textingConsentObtained={memberData?.textingConsentObtained || false}
          />

          <EditContactInfoModal
            open={contactModalOpen}
            onClose={() => setContactModalOpen(false)}
            contactInfo={memberData}
            onSave={handleSaveContactInfo}
            canWrite={canWriteModule}
          />
          <CopayCardModal
            open={isCopayCardModalOpen}
            onClose={() => setIsCopayCardModalOpen(false)}
            opportunity={selectedCopayOpportunity}
            memberData={memberData}
            viewOnly={isCopayCardModalViewOnly}
            initialCardData={copayCardModalInitialData}
            copayOpportunities={copayOpportunities}
            copayScenario={copayScenario}
            copayDetailsMap={copayDetailsMap}
            onPersistSuccess={(cardInfo) => {
              if (cardInfo) {
                const mappedData = {
                  copayBin: cardInfo.bin || cardInfo.copayBin || "",
                  copayPcn: cardInfo.pcn || cardInfo.copayPcn || "",
                  copayGrpId: cardInfo.grpId || cardInfo.copayGrpId || "",
                  copayMemberId:
                    cardInfo.memberId || cardInfo.copayMemberId || "",
                  copayExpirationDate:
                    cardInfo.expirationDate ||
                    cardInfo.copayExpirationDate ||
                    "",
                  copayExecutionStatus:
                    cardInfo.executionStatus ||
                    cardInfo.copayExecutionStatus ||
                    "COMPLETE",
                  copayRegistrationRequestStatus:
                    cardInfo.registrationRequestStatus ||
                    cardInfo.copayRegistrationRequestStatus ||
                    "",
                  annualBenefitAmount:
                    cardInfo.annualBenefitAmount !== undefined
                      ? cardInfo.annualBenefitAmount
                      : (cardInfo.copayAnnualBenefitAmount ?? null),
                  annualBenefitExpirationDate:
                    cardInfo.annualBenefitExpirationDate ||
                    cardInfo.copayAnnualBenefitExpirationDate ||
                    "",
                  annualBenefitNotes:
                    cardInfo.annualBenefitNotes ||
                    cardInfo.annualBenefitNote ||
                    cardInfo.copayAnnualBenefitNotes ||
                    "",
                  perFillAmount:
                    cardInfo.perFillAmount !== undefined
                      ? cardInfo.perFillAmount
                      : (cardInfo.copayPerFillAmount ?? null),
                  regUrl:
                    cardInfo.regUrl ||
                    cardInfo.copayRegUrl ||
                    selectedCopayOpportunity?.regUrl ||
                    selectedCopayOpportunity?.copayRegUrl ||
                    selectedCopayOpportunity?.program?.regUrl ||
                    selectedCopayOpportunity?.websiteUrl ||
                    selectedCopayOpportunity?.url ||
                    "",
                };
                setMemberData((prev) => ({
                  ...prev,
                  ...mappedData,
                }));
                const oppId =
                  selectedCopayOpportunity?.programId ||
                  selectedCopayOpportunity?.memberProgramId ||
                  selectedCopayOpportunity?.opportunityId ||
                  selectedCopayOpportunity?.id;
                if (oppId) {
                  setCopayDetailsMap((prev) => ({
                    ...prev,
                    [oppId]: mappedData,
                  }));
                }
                void fetchMemberData();
              }
            }}
          />
          <ClinicalHistoryModal
            open={isClinicalHistoryOpen}
            onClose={() => setIsClinicalHistoryOpen(false)}
            memberId={memberId}
            memberData={memberData}
            onReopen={handleReopenClinicalNote}
          />
          <BiosimilarsOutcomeModal
            open={isBiosimilarsOutcomeOpen}
            onClose={() => {
              setIsBiosimilarsOutcomeOpen(false);
              setSelectedBiosimilarsNote(null);
              setIsBiosimilarsReadOnly(false);
            }}
            onSuccess={async () => {
              setOpportunitiesReloadKey((prev) => prev + 1);
            }}
            memberId={memberId}
            readOnly={isBiosimilarsReadOnly}
            existingNote={selectedBiosimilarsNote}
          />
          <ClinicalNoteStartModal
            open={isClinicalNotePopupOpen}
            onClose={() => setIsClinicalNotePopupOpen(false)}
            onNext={handleClinicalNoteNext}
            isStarting={isStartingClinicalNote}
            programs={(() => {
              const list = [];
              const seenIds = new Set();
              const seenNames = new Set();

              const addProg = (id, name, originalObj = null) => {
                if (!id && !name) return;
                const upperId = String(id || "")
                  .toUpperCase()
                  .trim();
                const upperName = String(name || "")
                  .toUpperCase()
                  .trim();

                if (
                  upperId === "GENERAL" ||
                  upperId === "OTHER" ||
                  upperName === "GENERAL" ||
                  upperName === "OTHER" ||
                  upperId.includes("BIOSIMILAR") ||
                  upperName.includes("BIOSIMILAR") ||
                  checkBiosimilar(id) ||
                  checkBiosimilar(name) ||
                  checkBiosimilar(originalObj) ||
                  isNonClinicalProgram(id, name) ||
                  isNonClinicalProgram(originalObj) ||
                  hasTerminalOutcome(originalObj, assessmentReferenceData)
                ) {
                  return;
                }

                const isFormless = checkIsFormless(
                  { id: upperId, name: upperName, ...originalObj },
                  assessmentReferenceData,
                );

                const cleanName = upperName
                  .replaceAll("PROGRAM", "")
                  .replace(/[\s_-]+/g, "");
                const cleanId = upperId
                  .replaceAll("PROGRAM", "")
                  .replace(/[\s_-]+/g, "");

                const templateGroup = (clinicalNotePrograms || []).find((g) => {
                  const gType = String(g.groupType || "").toUpperCase();
                  if (gType === "GENERAL" || gType === "BIOSIMILARS")
                    return false;
                  const gId = String(g.programId || g.id || "")
                    .toUpperCase()
                    .replaceAll("PROGRAM", "")
                    .replace(/[\s_-]+/g, "");
                  const gName = String(
                    g.groupName || g.name || g.programDisplayName || "",
                  )
                    .toUpperCase()
                    .replaceAll("PROGRAM", "")
                    .replace(/[\s_-]+/g, "");
                  return (
                    (cleanId &&
                      (gId === cleanId ||
                        gId.includes(cleanId) ||
                        cleanId.includes(gId))) ||
                    (cleanName &&
                      (gName === cleanName ||
                        gName.includes(cleanName) ||
                        cleanName.includes(gName)))
                  );
                });

                // Do not add opportunities that are neither formless clinical programs nor matching active CG+ template groups
                if (!isFormless && !templateGroup) {
                  return;
                }

                const finalProgramId = isFormless
                  ? originalObj?.programCode ||
                    originalObj?.programId ||
                    id ||
                    name
                  : templateGroup?.programId ||
                    originalObj?.programId ||
                    id ||
                    name;
                const finalDisplayName = isFormless
                  ? originalObj?.programDisplayName ||
                    originalObj?.programName ||
                    originalObj?.displayName ||
                    originalObj?.name ||
                    originalObj?.groupName ||
                    name ||
                    id
                  : templateGroup?.programDisplayName ||
                    templateGroup?.groupName ||
                    templateGroup?.name ||
                    originalObj?.programDisplayName ||
                    originalObj?.programName ||
                    originalObj?.displayName ||
                    originalObj?.name ||
                    originalObj?.groupName ||
                    name ||
                    id;

                const normFinalId = String(finalProgramId).toUpperCase().trim();
                const normFinalName = String(finalDisplayName)
                  .toUpperCase()
                  .trim();

                if (
                  seenIds.has(normFinalId) ||
                  (normFinalName && seenNames.has(normFinalName))
                ) {
                  return;
                }
                seenIds.add(normFinalId);
                if (normFinalName) seenNames.add(normFinalName);

                list.push({
                  id: finalProgramId,
                  programId: finalProgramId,
                  name: finalDisplayName,
                  displayName: finalDisplayName,
                  groupName: finalDisplayName,
                  programDisplayName: finalDisplayName,
                  groupType: isFormless ? "FORMLESS" : "PROGRAM_SPECIFIC",
                  isFormless: isFormless,
                  formless: isFormless,
                  templates:
                    templateGroup?.templates || originalObj?.templates || [],
                });
              };

              if (
                Array.isArray(memberClinicalPrograms) &&
                memberClinicalPrograms.length > 0
              ) {
                memberClinicalPrograms.forEach((mp) => {
                  if (hasTerminalOutcome(mp, assessmentReferenceData)) {
                    return;
                  }
                  const pId = mp.programId || mp.id;
                  const pName =
                    mp.programDisplayName ||
                    mp.programName ||
                    mp.displayName ||
                    mp.name ||
                    mp.groupName;
                  addProg(pId, pName, mp);
                });
              }

              if (programMap && typeof programMap === "object") {
                Object.entries(programMap).forEach(([pId, pName]) => {
                  if (
                    checkBiosimilar(pId) ||
                    checkBiosimilar(pName) ||
                    isNonClinicalProgram(pId, pName)
                  ) {
                    return;
                  }
                  const hasTerminal = (memberClinicalPrograms || []).some(
                    (mp) => {
                      const mpId = mp.programId || mp.id;
                      const mpName =
                        mp.programDisplayName ||
                        mp.programName ||
                        mp.displayName ||
                        mp.name ||
                        mp.groupName;
                      const matches =
                        (pId &&
                          mpId &&
                          (cleanProgramId(mpId) === cleanProgramId(pId) ||
                            String(mpId).toUpperCase() ===
                              String(pId).toUpperCase())) ||
                        (pName &&
                          mpName &&
                          String(mpName).toUpperCase() ===
                            String(pName).toUpperCase());
                      return (
                        matches &&
                        hasTerminalOutcome(mp, assessmentReferenceData)
                      );
                    },
                  );
                  if (hasTerminal) return;
                  addProg(pId, pName);
                });
              }

              return list;
            })()}
            memberClinicalPrograms={memberClinicalPrograms}
            availablePrograms={clinicalNotePrograms}
            loading={isLoadingPrograms}
            referenceData={assessmentReferenceData}
            opportunityProgram={clinicalNoteOpportunityProgram}
          />
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </ContentContainer>
    </div>
  );
};

const PCADirectiveDisplay = ({ pcaDirective, pcaDirectiveEnabled }) => {
  const containerRef = useRef(null);
  const rulerRef = useRef(null);
  const [isMultiLine, setIsMultiLine] = useState(false);

  const displayText = pcaDirective || "N/A";

  useEffect(() => {
    if (!pcaDirectiveEnabled) return;

    const checkMultiLine = () => {
      if (!containerRef.current || !rulerRef.current) return;
      const availableWidth = containerRef.current.clientWidth;
      const textWidth = rulerRef.current.offsetWidth + 24;
      setIsMultiLine(textWidth > availableWidth);
    };

    checkMultiLine();

    const resizeObserver = new ResizeObserver(checkMultiLine);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [displayText, pcaDirectiveEnabled]);

  // Don't render if PCA directive is not enabled
  if (!pcaDirectiveEnabled) {
    return null;
  }

  const fontSize = isMultiLine ? "16px" : "18px";
  const lineHeight = isMultiLine ? "20px" : "22px";

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        minWidth: 0,
        maxWidth: "100%",
        width: "100%",
        justifyContent: "flex-end",
        position: "relative",
      }}
    >
      {/* Off-screen single-line measurement ruler at 18px */}
      <span
        ref={rulerRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          fontSize: "18px",
          fontWeight: 600,
          pointerEvents: "none",
          top: -9999,
          left: -9999,
        }}
      >
        PCA Directive: {displayText}
      </span>

      <Tooltip
        title={
          <Box
            sx={{
              maxHeight: "200px",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {displayText}
          </Box>
        }
        arrow
        placement="bottom"
        disableHoverListener={false}
        interactive
      >
        <Box
          sx={{
            backgroundColor: "#ffff",
            borderRadius: "10px",
            padding: "8px 12px",
            maxWidth: "100%",
            width: "fit-content",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize,
              lineHeight,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            PCA Directive:{" "}
            <span
              style={{
                color: "#CC0000",
                fontWeight: 600,
                fontSize,
              }}
            >
              {displayText}
            </span>
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

PCADirectiveDisplay.propTypes = {
  pcaDirective: PropTypes.string,
  pcaDirectiveEnabled: PropTypes.bool,
};

export default MemberDetails;
